import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import ProductPickerDialog from "../components/ProductPickerDialog";
import ConversationSummaryDialog from "../components/ConversationSummaryDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import {
  MessageSquare,
  Send,
  Search,
  Users,
  LogOut,
  MoreVertical,
  Smile,
  Paperclip,
  Info,
  Check,
  CheckCheck,
  Menu,
  X,
  UserPlus,
  Hash,
  Image as ImageIcon,
  FileText,
  Video,
  Download,
  Play,
  Loader2,
  Trash2,
  DollarSign,
  CreditCard,
  Mic,
  MicOff,
  MapPin,
  History,
  Pause,
  Ban,
  Star,
  StarOff,
  Link as LinkIcon,
  Copy,
  Reply,
  Forward,
  Edit3,
  Pin,
  Languages,
  Clock,
  Bell,
  Store,
  UtensilsCrossed,
  FolderOpen,
  Heart,
  Plus,
  Share2,
  Eye,
  EyeOff,
  ExternalLink,
  Sparkles,
  Package,
  ShoppingBag,
  Archive,
  Bookmark,
  Radio,
  Settings,
} from "lucide-react";

// Import new components
import { MessageActionMenu, TemporaryMessageTimer } from "../components/MessageActions";
import { SidebarSections } from "../components/SidebarSections";
import { NewChatDialog } from "../components/NewChatDialog";
import { SharedMediaPanel } from "../components/SharedMedia";
import { FavoriteMediaPicker } from "../components/FavoriteMedia";
import { NewMessagePopup } from "../components/NewMessagePopup";
import VerifiedBadge from "../components/VerifiedBadge";
import ScheduledMessagesDialog from "../components/ScheduledMessagesDialog";
import StickerPickerDialog from "../components/StickerPickerDialog";
import MiniAppPickerDialog from "../components/MiniAppPickerDialog";
import PremiumUpgradeDialog from "../components/PremiumUpgradeDialog";
import DevicesDialog from "../components/DevicesDialog";
import GamificationDialog from "../components/GamificationDialog";
import VoiceCallDialog from "../components/VoiceCallDialog";
import { Calendar, Sparkles as StickerIcon, Puzzle, BadgeCheck, Phone, Smartphone, Trophy } from "lucide-react";
import QRCode from "qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Extended emoji list
const EMOJI_LIST = [
  "👍", "❤️", "😂", "😮", "😢", "🎉", "🔥", "👏", "💯", "✨",
  "😍", "🥰", "😊", "😎", "🤔", "😅", "😭", "🙄", "😤", "🤯",
  "👀", "💪", "🙌", "👋", "🤝", "💕", "💖", "💗", "💘", "💝",
  "🎊", "🎁", "🏆", "⭐", "🌟", "💫", "✅", "❌", "⚡", "💡",
  "🙏", "😘", "🥺", "😌", "🤗", "😬", "🫠", "🥳", "😴", "🤑"
];

// Formato de última conexión estilo WhatsApp
const formatLastSeen = (lastSeen, status) => {
  if (status === 'online') return 'en línea';
  if (!lastSeen) return 'desconectado';
  
  const now = new Date();
  const lastSeenDate = new Date(lastSeen);
  const diffMs = now - lastSeenDate;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Formato de hora
  const timeStr = lastSeenDate.toLocaleTimeString('es-MX', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
  
  // Hoy
  if (lastSeenDate.toDateString() === now.toDateString()) {
    if (diffMins < 1) return 'últ. vez hace un momento';
    if (diffMins < 60) return `últ. vez hace ${diffMins} min`;
    return `últ. vez hoy ${timeStr}`;
  }
  
  // Ayer
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (lastSeenDate.toDateString() === yesterday.toDateString()) {
    return `últ. vez ayer ${timeStr}`;
  }
  
  // Esta semana (menos de 7 días)
  if (diffDays < 7) {
    const dayNames = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    return `últ. vez ${dayNames[lastSeenDate.getDay()]} ${timeStr}`;
  }
  
  // Más de una semana
  const dateStr = lastSeenDate.toLocaleDateString('es-MX', { 
    day: 'numeric', 
    month: 'short' 
  });
  return `últ. vez ${dateStr}`;
};

const ChatPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState(""); // Búsqueda separada para sidebar
  const [sidebarSearchResults, setSidebarSearchResults] = useState([]); // Resultados de búsqueda del sidebar
  const [okgoSearchQuery, setOkgoSearchQuery] = useState("");
  const [typingUsers, setTypingUsers] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showScheduledDialog, setShowScheduledDialog] = useState(false);
  const [showStickerDialog, setShowStickerDialog] = useState(false);
  const [showMiniAppDialog, setShowMiniAppDialog] = useState(false);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentCurrency, setPaymentCurrency] = useState("MXN");
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // GIF search
  const [gifSearchQuery, setGifSearchQuery] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [gifOffset, setGifOffset] = useState(0);
  const [gifHasMore, setGifHasMore] = useState(true);
  const [loadingMoreGifs, setLoadingMoreGifs] = useState(false);
  const gifScrollRef = useRef(null);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);

  // Media + GIF caption preview (WhatsApp-style: pick first, type caption, then send)
  const [mediaPreview, setMediaPreview] = useState(null);  // { fileData, fileType, isViewOnce, localUrl }
  const [mediaCaption, setMediaCaption] = useState("");
  const [gifPreview, setGifPreview] = useState(null);      // { url, title }
  const [gifCaption, setGifCaption] = useState("");
  
  // Walkie-Talkie Mode
  const [walkieTalkieMode, setWalkieTalkieMode] = useState(false);
  const [recordingType, setRecordingType] = useState("normal"); // "normal" or "walkie_talkie"
  const [activeInputTab, setActiveInputTab] = useState("chat"); // "chat" or "walkie_talkie"
  const [voiceSettings, setVoiceSettings] = useState({
    autoPlay: true,
    autoTranscribe: true,
    voiceFilter: "normal",
    autoTranslate: false,
    translateTo: "es",
    showTranscription: true
  });
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Auto-translation settings (per-account, persisted server-side)
  const [autoTranslateSettings, setAutoTranslateSettings] = useState({
    auto_translate_enabled: false,
    target_language: "es",
  });
  // Cache: message_id -> translated text (incoming messages translated to user's target lang)
  const [messageTranslations, setMessageTranslations] = useState({});

  // In-chat commerce
  const [showProductPicker, setShowProductPicker] = useState(false);
  // AI summary dialog
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const audioPlayersRef = useRef({});
  // Tracks which audio messages are currently playing so the play button can
  // turn green during playback and back to red when stopped.
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const audioContextRef = useRef(null);
  
  // Voice filter settings modal
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [selectedVoiceFilter, setSelectedVoiceFilter] = useState("normal");
  const [showVoiceFilterModal, setShowVoiceFilterModal] = useState(false);
  const [showTranslatorModal, setShowTranslatorModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Location sharing
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  
  // Mobile sidebar sheet
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Block user
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);
  
  // Favorites
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  
  // Global search
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ users: [], messages: [] });
  const [searchingGlobal, setSearchingGlobal] = useState(false);
  const [searchType, setSearchType] = useState("all");
  
  // Chat search (within current conversation)
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [chatSearchResults, setChatSearchResults] = useState([]);
  const [searchingChat, setSearchingChat] = useState(false);
  
  // New features state
  const [replyingTo, setReplyingTo] = useState(null);
  const [showSectionsPanel, setShowSectionsPanel] = useState(false);
  const [showSharedMedia, setShowSharedMedia] = useState(false);
  const [showTemporaryDialog, setShowTemporaryDialog] = useState(false);
  const [temporaryMinutes, setTemporaryMinutes] = useState(15);
  const [temporaryContent, setTemporaryContent] = useState("");
  const [platformDomain, setPlatformDomain] = useState("okchat.okgo.mx");
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [showFavoriteMedia, setShowFavoriteMedia] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [savingToCategory, setSavingToCategory] = useState(false);
  const [profileQrCode, setProfileQrCode] = useState(null);
  
  // New message popup notification
  const [newMessagePopup, setNewMessagePopup] = useState(null);
  
  // Pagination state for performance optimization
  const [conversationsPage, setConversationsPage] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const CONVERSATIONS_PAGE_SIZE = 30;
  const MESSAGES_PAGE_SIZE = 50;
  
  // View once media
  const [viewOnceMode, setViewOnceMode] = useState(false);
  const [viewOnceInputRef, setViewOnceInputRef] = useState(null);
  const [showViewOnceViewer, setShowViewOnceViewer] = useState(false);
  const [viewOnceMedia, setViewOnceMedia] = useState(null);
  const [viewedOnceMessages, setViewedOnceMessages] = useState(new Set());
  // Wave 3 — multi-device, gamification, voice calls
  const [showDevicesDialog, setShowDevicesDialog] = useState(false);
  const [showGamificationDialog, setShowGamificationDialog] = useState(false);
  const [activeCall, setActiveCall] = useState(null); // { mode, callId, peer, incomingOffer }
  
  const ws = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const pttTouchActiveRef = useRef(false);
  const selectedConversationRef = useRef(null);
  const viewOnceImageRef = useRef(null);
  const viewOnceVideoRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Check for payment success/cancel
  useEffect(() => {
    const paymentSuccess = searchParams.get("payment_success");
    const sessionId = searchParams.get("session_id");
    
    if (paymentSuccess === "true" && sessionId) {
      fetch(`${API}/payments/status/${sessionId}`, { credentials: "include" })
        .then(res => res.json())
        .then(data => {
          if (data.payment_status === "paid") {
            toast.success(`¡Pago de $${data.amount} ${data.currency.toUpperCase()} completado!`);
          }
        })
        .catch(console.error);
      navigate("/chat", { replace: true });
    }
    
    if (searchParams.get("payment_cancelled") === "true") {
      toast.info("Pago cancelado");
      navigate("/chat", { replace: true });
    }
  }, [searchParams, navigate]);

  // Fetch currencies
  useEffect(() => {
    fetch(`${API}/currencies`, { credentials: "include" })
      .then(res => res.json())
      .then(data => {
        setCurrencies(data.currencies || []);
        setPaymentCurrency(data.default || "MXN");
      })
      .catch(console.error);
  }, []);

  // Fetch platform config
  useEffect(() => {
    fetch(`${API}/platform/config`, { credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.domain) setPlatformDomain(data.domain);
      })
      .catch(console.error);
  }, []);

  // Request notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Send desktop notification
  const sendDesktopNotification = useCallback((title, body, icon) => {
    if ("Notification" in window && Notification.permission === "granted") {
      // Only notify if window is not focused
      if (document.hidden) {
        const notification = new Notification(title, {
          body,
          icon: icon || "/logo-okgo-footer.png",
          badge: "/logo-okgo-footer.png",
          tag: "okchat-message",
          renotify: true
        });
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    }
  }, []);

  // Scroll to specific message (for search "go to message" feature)
  const scrollToMessage = useCallback((messageId) => {
    setHighlightedMessageId(messageId);
    setTimeout(() => {
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
        messageElement.classList.add("highlight-animation");
        setTimeout(() => {
          messageElement.classList.remove("highlight-animation");
          setHighlightedMessageId(null);
        }, 2000);
      }
    }, 100);
  }, []);

  // Fetch conversations with pagination
  const fetchConversations = useCallback(async (reset = true) => {
    try {
      const skip = reset ? 0 : conversations.length;
      if (!reset) setLoadingMoreConversations(true);
      
      const response = await fetch(
        `${API}/conversations?limit=${CONVERSATIONS_PAGE_SIZE}&skip=${skip}`, 
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (reset) {
          setConversations(data);
          setConversationsPage(1);
        } else {
          setConversations(prev => [...prev, ...data]);
          setConversationsPage(prev => prev + 1);
        }
        setHasMoreConversations(data.length === CONVERSATIONS_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingMoreConversations(false);
    }
  }, [conversations.length]);

  // Load more conversations (for infinite scroll)
  const loadMoreConversations = useCallback(() => {
    if (!loadingMoreConversations && hasMoreConversations) {
      fetchConversations(false);
    }
  }, [fetchConversations, loadingMoreConversations, hasMoreConversations]);

  // Fetch users
  const fetchUsers = useCallback(async (search = "") => {
    try {
      const response = await fetch(`${API}/users?search=${search}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.filter(u => u.user_id !== user?.user_id));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [user]);

  // Fetch messages with pagination support for loading older messages
  const fetchMessages = useCallback(async (convId, loadOlder = false) => {
    try {
      if (loadOlder) setLoadingMoreMessages(true);
      
      const beforeParam = loadOlder && messages.length > 0 
        ? `&before=${messages[0].created_at}` 
        : '';
      
      const response = await fetch(
        `${API}/conversations/${convId}/messages?limit=${MESSAGES_PAGE_SIZE}${beforeParam}`, 
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (loadOlder) {
          // Prepend older messages
          setMessages(prev => [...data, ...prev]);
        } else {
          setMessages(data);
        }
        setMessagesHasMore(data.length === MESSAGES_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [messages]);

  // Fetch payment history
  const fetchPaymentHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API}/payments/history`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch trending GIFs (with pagination support)
  const fetchTrendingGifs = async (reset = true) => {
    if (reset) {
      setLoadingGifs(true);
      setGifOffset(0);
      setGifHasMore(true);
    }
    try {
      const offset = reset ? 0 : gifOffset;
      const response = await fetch(
        `${API}/gifs/trending?limit=50&offset=${offset}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        const newGifs = data.gifs || [];
        if (reset) {
          setGifs(newGifs);
        } else {
          setGifs((prev) => [...prev, ...newGifs]);
        }
        setGifOffset(offset + newGifs.length);
        setGifHasMore(newGifs.length >= 50);
      }
    } catch (error) {
      console.error("Error fetching trending GIFs:", error);
    } finally {
      setLoadingGifs(false);
    }
  };

  // Search GIFs (with pagination support)
  const searchGifs = async (query, reset = true) => {
    if (!query.trim()) {
      fetchTrendingGifs(true);
      return;
    }
    if (reset) {
      setLoadingGifs(true);
      setGifOffset(0);
      setGifHasMore(true);
    }
    try {
      const offset = reset ? 0 : gifOffset;
      const response = await fetch(
        `${API}/gifs/search?q=${encodeURIComponent(query)}&limit=50&offset=${offset}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        const newGifs = data.gifs || [];
        if (reset) {
          setGifs(newGifs);
        } else {
          setGifs((prev) => [...prev, ...newGifs]);
        }
        setGifOffset(offset + newGifs.length);
        setGifHasMore(newGifs.length >= 50);
      }
    } catch (error) {
      console.error("Error searching GIFs:", error);
    } finally {
      setLoadingGifs(false);
    }
  };

  // Load more GIFs (called when user scrolls near bottom of GIF picker)
  const loadMoreGifs = async () => {
    if (loadingMoreGifs || !gifHasMore) return;
    setLoadingMoreGifs(true);
    try {
      const offset = gifOffset;
      const url = gifSearchQuery.trim()
        ? `${API}/gifs/search?q=${encodeURIComponent(gifSearchQuery)}&limit=50&offset=${offset}`
        : `${API}/gifs/trending?limit=50&offset=${offset}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const newGifs = data.gifs || [];
        setGifs((prev) => [...prev, ...newGifs]);
        setGifOffset(offset + newGifs.length);
        setGifHasMore(newGifs.length >= 50);
      }
    } catch (error) {
      console.error("Error loading more GIFs:", error);
    } finally {
      setLoadingMoreGifs(false);
    }
  };

  // Handler para scroll infinito en el picker de GIFs
  const handleGifScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      loadMoreGifs();
    }
  };

  // ===== Auto-translation: load user preferences once =====
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await fetch(`${API}/user/translation-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token') || ''}` },
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setAutoTranslateSettings({
            auto_translate_enabled: !!data.auto_translate_enabled,
            target_language: data.target_language || "es",
          });
        }
      } catch (_) {}
    })();
  }, [user?.user_id]);

  // ===== Auto-translation: bulk-translate untranslated incoming text/url messages =====
  useEffect(() => {
    if (!autoTranslateSettings.auto_translate_enabled) return;
    if (!user || !messages || messages.length === 0) return;

    const target = autoTranslateSettings.target_language;
    const pending = [];
    for (const m of messages) {
      if (!m || !m.message_id) continue;
      if (m.sender_id === user.user_id) continue; // don't translate own messages
      if (!["text", "url"].includes(m.message_type)) continue;
      if (!m.content || typeof m.content !== "string") continue;
      if (m.content.length > 800) continue; // skip giant payloads
      if (messageTranslations[m.message_id]) continue;
      pending.push({ id: m.message_id, text: m.content });
      if (pending.length >= 40) break;
    }
    if (pending.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/translate/batch`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`,
          },
          credentials: "include",
          body: JSON.stringify({ items: pending, target_language: target }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data.translations) return;
        setMessageTranslations((prev) => ({ ...prev, ...data.translations }));
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [messages, autoTranslateSettings, user?.user_id]);

  // Whenever the target language changes, drop cached translations so the next
  // pass re-translates incoming messages into the newly selected language.
  useEffect(() => {
    setMessageTranslations({});
  }, [autoTranslateSettings.target_language]);

  // Persist auto-translate preference changes to the server.
  const saveTranslationSettings = useCallback(async (next) => {
    setAutoTranslateSettings(next);
    try {
      await fetch(`${API}/user/translation-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`,
        },
        credentials: "include",
        body: JSON.stringify({
          auto_translate_enabled: next.auto_translate_enabled,
          target_language: next.target_language,
        }),
      });
    } catch (_) {}
  }, []);

  // Stable refs to fetchers so the WebSocket effect doesn't re-run (and tear
  // down the socket) every time `messages`/`conversations` change. The WS
  // effect must only depend on `user.user_id`, otherwise reconnect-churn
  // itself becomes the reliability problem.
  const fetchConversationsRef = useRef(fetchConversations);
  const fetchMessagesRef = useRef(fetchMessages);
  useEffect(() => { fetchConversationsRef.current = fetchConversations; }, [fetchConversations]);
  useEffect(() => { fetchMessagesRef.current = fetchMessages; }, [fetchMessages]);

  // WebSocket connection with improved message handling.
  //
  // Robustness layer for real-time delivery (walkie-talkie + chat):
  //   * Catch-up on (re)connect: refetch the active conversation and the
  //     conversations list right after auth_ok so any messages broadcast while
  //     the socket was momentarily down still appear.
  //   * Heartbeat: send {type:"ping"} every 20s and force-reconnect if we don't
  //     hear back within 10s. This detects half-open / silently-dead sockets
  //     (mobile sleep, OS network change, proxy idle timeout) much faster than
  //     waiting for the TCP keepalive.
  //   * visibilitychange: when the tab returns to foreground, refetch and ping
  //     immediately so the user always sees the latest state.
  //   * Lifecycle guards: an `intentionalClose` flag prevents cleanup-induced
  //     `onclose` from spawning zombie reconnects, and we never start a new
  //     socket while one is already CONNECTING/OPEN.
  useEffect(() => {
    if (!user) return;

    let pingInterval = null;
    let pongWatchdog = null;
    let reconnectTimer = null;
    let intentionalClose = false;

    const clearTimers = () => {
      if (pingInterval) { clearInterval(pingInterval); pingInterval = null; }
      if (pongWatchdog) { clearTimeout(pongWatchdog); pongWatchdog = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };

    const catchUp = () => {
      try { fetchConversationsRef.current && fetchConversationsRef.current(); } catch (e) {}
      const conv = selectedConversationRef.current;
      if (conv && conv.conversation_id && fetchMessagesRef.current) {
        try { fetchMessagesRef.current(conv.conversation_id); } catch (e) {}
      }
    };

    const connectWebSocket = () => {
      // Guard against parallel connects: if an existing socket is already
      // CONNECTING or OPEN, do nothing.
      const existing = ws.current;
      if (existing && (existing.readyState === WebSocket.CONNECTING || existing.readyState === WebSocket.OPEN)) {
        return;
      }
      clearTimers();
      intentionalClose = false;
      const wsUrl = BACKEND_URL.replace(/^http/, 'ws');
      const socket = new WebSocket(`${wsUrl}/api/ws/${user.user_id}`);

      const sendPing = () => {
        if (socket.readyState !== WebSocket.OPEN) return;
        try { socket.send(JSON.stringify({ type: "ping" })); } catch (e) {}
        // If we don't receive ANY message back within 10s, treat as dead
        if (pongWatchdog) clearTimeout(pongWatchdog);
        pongWatchdog = setTimeout(() => {
          console.warn("WS heartbeat timeout — forcing reconnect");
          try { socket.close(); } catch (e) {}
        }, 10000);
      };

      socket.onopen = () => {
        // Auth por primer mensaje: el token NUNCA viaja en la URL para evitar
        // que quede en logs de proxies, historial del navegador o cabeceras Referer.
        // El backend valida el token y que pertenezca a este user_id; si falla cierra con 1008.
        const token = localStorage.getItem('session_token') || '';
        try { socket.send(JSON.stringify({ type: "auth", token })); } catch (e) {}
        console.log("WebSocket connected, auth sent");
        // Start heartbeat (every 20s)
        if (pingInterval) clearInterval(pingInterval);
        pingInterval = setInterval(sendPing, 20000);
      };

      socket.onmessage = (event) => {
        // Any incoming frame proves the socket is alive — clear the watchdog
        if (pongWatchdog) { clearTimeout(pongWatchdog); pongWatchdog = null; }
        const data = JSON.parse(event.data);
        console.log("WebSocket received:", data.type, data);

        // Catch-up after successful authentication
        if (data.type === "auth_ok") {
          catchUp();
          return;
        }
        if (data.type === "pong") {
          return;
        }

        // Wave 3 — incoming voice call
        if (data.type === "call_offer") {
          setActiveCall((prev) => prev ? prev : ({
            mode: "incoming",
            callId: data.call_id,
            peer: data.from_user || { user_id: data.from_user_id, name: "Usuario" },
            incomingOffer: data.sdp,
          }));
          return;
        }
        if (data.type === "call_answer" || data.type === "call_ice" || data.type === "call_end" || data.type === "call_reject") {
          // Forwarded to VoiceCallDialog via its own message listener.
          return;
        }
        if (data.type === "gamification_update") {
          if (data.level_up) toast.success(`¡Subiste al nivel ${data.level}! 🎉`);
          (data.new_achievements || []).forEach((a) => toast.success(`${a.icon} Logro desbloqueado: ${a.name}`));
          return;
        }

        if (data.type === "new_message") {
          const newMsg = data.message;
          const currentConv = selectedConversationRef.current;
          
          // Always update conversations list to show latest message
          fetchConversations();
          
          // Only add to messages if it's for the currently selected conversation
          if (currentConv && currentConv.conversation_id === newMsg.conversation_id) {
            setMessages(prev => {
              // Prevent duplicates
              if (prev.some(m => m.message_id === newMsg.message_id)) {
                return prev;
              }
              return [...prev, newMsg];
            });
          }
          
          // Show popup notification for messages from others when not in that conversation
          if (newMsg.sender_id !== user?.user_id) {
            const isInDifferentConv = !currentConv || currentConv.conversation_id !== newMsg.conversation_id;
            
            if (isInDifferentConv) {
              // Show in-app popup
              setNewMessagePopup({
                senderName: newMsg.sender_info?.name || "Usuario",
                senderPicture: newMsg.sender_info?.picture,
                message: newMsg.content?.substring(0, 100) || "Te envió un mensaje",
                conversationId: newMsg.conversation_id,
                senderId: newMsg.sender_id
              });
            }
            
            // Also send desktop notification
            sendDesktopNotification(
              newMsg.sender_info?.name || "Nuevo mensaje",
              newMsg.content?.substring(0, 100) || "Tienes un nuevo mensaje",
              newMsg.sender_info?.picture
            );
          }
        } else if (data.type === "typing") {
          setTypingUsers(prev => ({
            ...prev,
            [data.conversation_id]: data.is_typing ? data.user_id : null
          }));
        } else if (data.type === "reaction_update") {
          setMessages(prev => prev.map(msg => 
            msg.message_id === data.message_id ? { ...msg, reactions: data.reactions } : msg
          ));
        } else if (data.type === "read_receipt" || data.type === "message_read") {
          // Recipient opened the message — switch sender's UI to "read" (double green check)
          setMessages(prev => prev.map(msg =>
            msg.message_id === data.message_id
              ? { ...msg, read_by: [...new Set([...(msg.read_by || []), data.read_by || data.user_id])] }
              : msg
          ));
        } else if (data.type === "message_delivered") {
          // Recipient's device received the message via WS — switch sender's UI from ✓ to ✓✓
          setMessages(prev => prev.map(msg =>
            msg.message_id === data.message_id
              ? { ...msg, delivered_to: [...new Set([...(msg.delivered_to || []), ...(data.delivered_to || [])])] }
              : msg
          ));
        } else if (data.type === "message_deleted") {
          setMessages(prev => prev.map(msg =>
            msg.message_id === data.message_id
              ? { ...msg, is_deleted: true, content: "Mensaje eliminado" }
              : msg
          ));
        } else if (data.type === "message_edited") {
          setMessages(prev => prev.map(msg =>
            msg.message_id === data.message_id
              ? {
                  ...msg,
                  content: data.content !== undefined ? data.content : msg.content,
                  edited_at: data.edited_at,
                  // Walkie-talkie transcription arrives via this event after async whisper finishes
                  transcription: data.transcription !== undefined ? data.transcription : msg.transcription
                }
              : msg
          ));
        } else if (data.type === "message_pinned") {
          setMessages(prev => prev.map(msg =>
            msg.message_id === data.message_id
              ? { ...msg, is_pinned: true, pinned_by: data.pinned_by }
              : msg
          ));
          toast.info("Mensaje fijado");
        } else if (data.type === "message_unpinned") {
          setMessages(prev => prev.map(msg =>
            msg.message_id === data.message_id
              ? { ...msg, is_pinned: false }
              : msg
          ));
        } else if (data.type === "walkie_talkie" || data.type === "normal") {
          // Handle incoming voice message (walkie-talkie or normal audio)
          const newMsg = data.message;
          const currentConv = selectedConversationRef.current;
          
          // Update conversations list
          fetchConversations();
          
          // Add to messages if in current conversation
          if (currentConv && currentConv.conversation_id === newMsg.conversation_id) {
            setMessages(prev => {
              if (prev.some(m => m.message_id === newMsg.message_id)) {
                return prev;
              }
              return [...prev, newMsg];
            });
          }
          
          // Auto-play only for walkie-talkie if enabled
          if (data.auto_play && data.type === "walkie_talkie" && voiceSettings.autoPlay && newMsg.voice_url) {
            // Play the voice message directly
            setTimeout(() => {
              playWalkieTalkieMessage(newMsg.voice_url, newMsg.message_id);
            }, 100);
            
            // Show toast with transcription if available
            if (newMsg.transcription) {
              toast.info(`🎤 ${newMsg.sender_info?.name}: "${newMsg.transcription.slice(0, 50)}..."`, {
                duration: 5000
              });
            } else {
              toast.info(`🎤 ${newMsg.sender_info?.name} envió un mensaje de voz`, {
                duration: 3000
              });
            }
          } else if (data.type === "normal" && newMsg.voice_url) {
            // Just show toast for normal audio
            toast.info(`🎙️ ${newMsg.sender_info?.name} envió un audio`, {
              duration: 3000
            });
          }
        }
      };

      socket.onclose = () => {
        clearTimers();
        if (intentionalClose) {
          console.log("WebSocket closed (intentional) — no reconnect");
          return;
        }
        console.log("WebSocket disconnected, reconnecting in 1.5s...");
        reconnectTimer = setTimeout(connectWebSocket, 1500);
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        // Don't reconnect here — onclose will fire next and handle it
      };

      ws.current = socket;
    };

    connectWebSocket();

    // When the tab returns to foreground, immediately catch up and verify the
    // socket is alive. Mobile browsers and laptop sleep frequently kill WS
    // connections silently — this guarantees the user sees latest state ASAP.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      catchUp();
      const sock = ws.current;
      // Only reconnect if there's no socket or it's actually closed/closing.
      // Never restart while CONNECTING/OPEN — connectWebSocket() also guards.
      if (!sock || sock.readyState === WebSocket.CLOSED || sock.readyState === WebSocket.CLOSING) {
        connectWebSocket();
      } else if (sock.readyState === WebSocket.OPEN) {
        try { sock.send(JSON.stringify({ type: "ping" })); } catch (e) {}
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onVisibilityChange);
    window.addEventListener("online", onVisibilityChange);

    return () => {
      intentionalClose = true;
      clearTimers();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onVisibilityChange);
      window.removeEventListener("online", onVisibilityChange);
      if (ws.current) {
        try { ws.current.close(); } catch (e) {}
      }
    };
  }, [user?.user_id]);

  // Initial data load
  useEffect(() => {
    fetchConversations();
    fetchUsers();
    fetchFavorites();
  }, [fetchConversations, fetchUsers]);

  // Fetch favorites
  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API}/user/favorites`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  // Toggle favorite
  const toggleFavorite = async (userId, isFavorite) => {
    try {
      const endpoint = isFavorite ? "/user/favorites/remove" : "/user/favorites/add";
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId }),
      });

      if (response.ok) {
        if (isFavorite) {
          setFavorites(prev => prev.filter(f => f.user_id !== userId));
          toast.success("Eliminado de favoritos");
        } else {
          fetchFavorites();
          toast.success("Agregado a favoritos");
        }
      }
    } catch (error) {
      toast.error("Error al actualizar favoritos");
    }
  };

  // Global search
  const handleGlobalSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ users: [], messages: [] });
      return;
    }

    setSearchingGlobal(true);
    try {
      const params = new URLSearchParams({
        q: query,
        search_type: searchType,
        limit: "20"
      });

      const response = await fetch(`${API}/search?${params}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setSearchingGlobal(false);
    }
  };

  // Search within conversation
  const handleChatSearch = async (query) => {
    if (!query || query.length < 2 || !selectedConversation) {
      setChatSearchResults([]);
      return;
    }

    setSearchingChat(true);
    try {
      const params = new URLSearchParams({
        q: query,
        limit: "50"
      });

      const response = await fetch(
        `${API}/search/conversation/${selectedConversation.conversation_id}?${params}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setChatSearchResults(data.messages);
      }
    } catch (error) {
      console.error("Error searching in chat:", error);
    } finally {
      setSearchingChat(false);
    }
  };

  // Debounced search handlers
  useEffect(() => {
    const timer = setTimeout(() => {
      if (globalSearchQuery) {
        handleGlobalSearch(globalSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearchQuery, searchType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (chatSearchQuery) {
        handleChatSearch(chatSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [chatSearchQuery]);

  // Búsqueda de usuarios en el sidebar
  useEffect(() => {
    const searchSidebarUsers = async () => {
      if (sidebarSearchQuery.trim().length < 2) {
        setSidebarSearchResults([]);
        return;
      }
      
      try {
        const response = await fetch(
          `${API}/search?q=${encodeURIComponent(sidebarSearchQuery)}&search_type=users`,
          { 
            headers: { ...getAuthHeaders() },
            credentials: "include" 
          }
        );
        if (response.ok) {
          const data = await response.json();
          setSidebarSearchResults(data.users || []);
        }
      } catch (err) {
        console.error("Error searching sidebar:", err);
      }
    };
    
    const timer = setTimeout(searchSidebarUsers, 300);
    return () => clearTimeout(timer);
  }, [sidebarSearchQuery]);

  // Load conversation from URL
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.conversation_id === conversationId);
      if (conv) {
        setSelectedConversation(conv);
        fetchMessages(conversationId);
      }
    }
  }, [conversationId, conversations, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load trending GIFs when GIF picker opens
  useEffect(() => {
    if (showGifPicker) {
      fetchTrendingGifs();
    }
  }, [showGifPicker]);

  // Handle file upload — uploads first, then shows a preview modal where the user can
  // type an optional caption (WhatsApp-style) before sending. View-once still sends
  // immediately because the UX intent is "ephemeral, no extras".
  const handleFileUpload = async (e, fileType = "any", isViewOnce = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setShowAttachMenu(false);

    // Build a local preview URL so the user sees what they're about to send
    // even before the upload finishes.
    const localUrl = URL.createObjectURL(file);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (isViewOnce) {
          // View-once: send immediately, no caption (matches the ephemeral intent)
          await sendMessageWithFile(data, true, "");
          toast.success("Mensaje de una sola vista enviado");
          URL.revokeObjectURL(localUrl);
        } else {
          // Open the caption preview modal — sending happens when user confirms
          setMediaCaption("");
          setMediaPreview({
            fileData: data,
            fileType: data.file_type,
            isViewOnce: false,
            localUrl,
            originalName: file.name,
          });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al subir archivo");
        URL.revokeObjectURL(localUrl);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error al subir archivo");
      URL.revokeObjectURL(localUrl);
    } finally {
      setUploading(false);
      setViewOnceMode(false);
      // Reset all file inputs so picking the same file twice still triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (imageInputRef.current) imageInputRef.current.value = "";
      if (videoInputRef.current) videoInputRef.current.value = "";
      if (viewOnceImageRef.current) viewOnceImageRef.current.value = "";
      if (viewOnceVideoRef.current) viewOnceVideoRef.current.value = "";
    }
  };

  // Confirm send from the media-preview modal
  const confirmSendMedia = async () => {
    if (!mediaPreview) return;
    const { fileData, isViewOnce, localUrl } = mediaPreview;
    const caption = mediaCaption.trim();
    try {
      await sendMessageWithFile(fileData, isViewOnce, caption);
      toast.success("Archivo enviado");
    } catch (e) {
      toast.error("Error al enviar archivo");
    } finally {
      URL.revokeObjectURL(localUrl);
      setMediaPreview(null);
      setMediaCaption("");
    }
  };

  const cancelMediaPreview = () => {
    if (mediaPreview?.localUrl) URL.revokeObjectURL(mediaPreview.localUrl);
    setMediaPreview(null);
    setMediaCaption("");
  };

  // Generate QR code when user profile is opened
  useEffect(() => {
    const generateQR = async () => {
      if (selectedUserProfile?.nickname && showUserProfile) {
        try {
          const profileUrl = `${window.location.origin}/u/${selectedUserProfile.nickname}`;
          const qr = await QRCode.toDataURL(profileUrl, {
            width: 200,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
          });
          setProfileQrCode(qr);
        } catch (err) {
          console.error("QR generation error:", err);
        }
      }
    };
    generateQR();
  }, [selectedUserProfile, showUserProfile]);

  // Send message with file - now accepts an optional WhatsApp-style caption
  const sendMessageWithFile = async (fileData, isViewOnce = false, caption = "") => {
    if (!selectedConversation) return;

    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          content: fileData.file_name || "Archivo",
          message_type: fileData.file_type,
          file_url: fileData.file_url,
          file_name: fileData.file_name,
          file_size: fileData.file_size,
          is_view_once: isViewOnce,
          caption: caption || null,
        }),
      });

      if (!response.ok) {
        toast.error("Error al enviar archivo");
      }
      // Message will appear via WebSocket
    } catch (error) {
      console.error("Error sending file message:", error);
      toast.error("Error al enviar archivo");
    }
  };

  // Open GIF preview modal so the user can type a caption before sending
  const sendGif = (gifUrl, gifTitle = "") => {
    if (!selectedConversation) return;
    setShowGifPicker(false);
    setGifSearchQuery("");
    setGifCaption("");
    setGifPreview({ url: gifUrl, title: gifTitle });
  };

  // Confirm send from the GIF preview modal
  const confirmSendGif = async () => {
    if (!gifPreview || !selectedConversation) return;
    const { url } = gifPreview;
    const caption = gifCaption.trim();
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          content: url,
          message_type: "gif",
          file_url: url,
          caption: caption || null,
        }),
      });

      if (!response.ok) {
        toast.error("Error al enviar GIF");
      }
    } catch (error) {
      console.error("Error sending GIF:", error);
      toast.error("Error al enviar GIF");
    } finally {
      setGifPreview(null);
      setGifCaption("");
    }
  };

  const cancelGifPreview = () => {
    setGifPreview(null);
    setGifCaption("");
  };

  // Web Audio API Voice Filters
  const createVoiceFilter = (audioContext, sourceNode, filterType) => {
    switch (filterType) {
      case "robot": {
        // Robot effect - modulation with oscillator
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const distortion = audioContext.createWaveShaper();
        
        oscillator.frequency.value = 50;
        oscillator.type = "square";
        gainNode.gain.value = 0.5;
        
        // Create distortion curve
        const curve = new Float32Array(256);
        for (let i = 0; i < 256; i++) {
          const x = (i / 128) - 1;
          curve[i] = (Math.PI + 100) * x / (Math.PI + 100 * Math.abs(x));
        }
        distortion.curve = curve;
        
        sourceNode.connect(distortion);
        return distortion;
      }
      
      case "echo": {
        // Echo effect - delay with feedback
        const delay = audioContext.createDelay(1.0);
        const feedback = audioContext.createGain();
        const dryGain = audioContext.createGain();
        const wetGain = audioContext.createGain();
        const merger = audioContext.createChannelMerger(2);
        
        delay.delayTime.value = 0.3;
        feedback.gain.value = 0.4;
        dryGain.gain.value = 0.8;
        wetGain.gain.value = 0.6;
        
        sourceNode.connect(dryGain);
        sourceNode.connect(delay);
        delay.connect(feedback);
        feedback.connect(delay);
        delay.connect(wetGain);
        
        const output = audioContext.createGain();
        dryGain.connect(output);
        wetGain.connect(output);
        
        return output;
      }
      
      case "squirrel": {
        // Squirrel/chipmunk effect - pitch shift up
        const playbackRate = 1.5;
        // For real pitch shifting, we'd need a more complex solution
        // This is a simplified version using playback rate change
        const compressor = audioContext.createDynamicsCompressor();
        compressor.threshold.value = -20;
        compressor.knee.value = 40;
        compressor.ratio.value = 12;
        sourceNode.connect(compressor);
        return compressor;
      }
      
      case "deep": {
        // Deep voice effect - low pass filter + lower pitch simulation
        const lowpass = audioContext.createBiquadFilter();
        const gainBoost = audioContext.createGain();
        
        lowpass.type = "lowpass";
        lowpass.frequency.value = 800;
        lowpass.Q.value = 1;
        gainBoost.gain.value = 1.3;
        
        sourceNode.connect(lowpass);
        lowpass.connect(gainBoost);
        return gainBoost;
      }
      
      default:
        return sourceNode;
    }
  };

  // Voice recording functions
  const startRecording = async (type = "normal") => {
    try {
      setRecordingType(type);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create audio context for voice filters if needed
      const filterType = type === "walkie_talkie" ? selectedVoiceFilter : "normal";
      let processedStream = stream;
      
      if (filterType !== "normal") {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        
        const source = audioContext.createMediaStreamSource(stream);
        const destination = audioContext.createMediaStreamDestination();
        
        const filteredNode = createVoiceFilter(audioContext, source, filterType);
        filteredNode.connect(destination);
        
        processedStream = destination.stream;
      }
      
      const recorder = new MediaRecorder(processedStream, { mimeType: 'audio/webm' });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        await sendVoiceMessage(audioBlob, type, filterType);
        stream.getTracks().forEach(track => track.stop());
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
    }
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
    setRecordingTime(0);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };

  // Walkie-Talkie: Send voice message with transcription
  const sendVoiceMessage = async (audioBlob, type = "walkie_talkie", filterUsed = "normal") => {
    if (!selectedConversation) return;
    
    setUploading(true);
    // Only transcribe for walkie-talkie mode
    const shouldTranscribe = type === "walkie_talkie" && voiceSettings.autoTranscribe;
    setIsTranscribing(shouldTranscribe);
    
    try {
      const formData = new FormData();
      formData.append("conversation_id", selectedConversation.conversation_id);
      formData.append("audio", audioBlob, type === "walkie_talkie" ? "walkie_talkie.webm" : "audio.webm");
      formData.append("auto_transcribe", shouldTranscribe.toString());
      formData.append("message_type", type);
      formData.append("voice_filter", filterUsed);
      formData.append("auto_translate", voiceSettings.autoTranslate.toString());
      formData.append("translate_to", voiceSettings.translateTo);
      
      const response = await fetch(`${API}/voice/send`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        const filterName = filterUsed !== "normal" ? ` (${filterUsed})` : "";

        // Optimistic insert for sender (backend WebSocket broadcast skips sender,
        // so without this the sender wouldn't see their own voice message until refresh).
        if (data.message_id && selectedConversation) {
          const optimisticMsg = {
            message_id: data.message_id,
            conversation_id: selectedConversation.conversation_id,
            sender_id: user?.user_id,
            content: data.transcription || "",
            message_type: type,
            voice_url: data.voice_url,
            voice_duration: data.duration,
            transcription: data.transcription || null,
            created_at: new Date().toISOString(),
            read_by: [user?.user_id],
            is_encrypted: false,
            sender_info: {
              user_id: user?.user_id,
              name: user?.name,
              picture: user?.picture,
              nickname: user?.nickname,
            },
          };
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === optimisticMsg.message_id)) return prev;
            return [...prev, optimisticMsg];
          });
          fetchConversations();
        }

        if (type === "walkie_talkie") {
          toast.success(
            data.transcription 
              ? `🎤 Enviado${filterName}: "${data.transcription.slice(0, 30)}..."` 
              : `🎤 Mensaje de voz enviado${filterName}`
          );
        } else {
          toast.success("🎙️ Audio enviado");
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar mensaje de voz");
      }
    } catch (error) {
      console.error("Error sending voice message:", error);
      toast.error("Error al enviar mensaje de voz");
    } finally {
      setUploading(false);
      setIsTranscribing(false);
      setRecordingTime(0);
    }
  };

  // Translate a voice message
  const translateVoiceMessage = async (voiceId, targetLanguage = "es") => {
    try {
      const formData = new FormData();
      formData.append("voice_id", voiceId);
      formData.append("target_language", targetLanguage);
      
      const response = await fetch(`${API}/voice/translate`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Traducción completada");
        return data;
      } else {
        toast.error("Error al traducir");
        return null;
      }
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Error de traducción");
      return null;
    }
  };

  // Convert audio to text only (without sending)
  const transcribeAudioOnly = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.webm");
      
      const response = await fetch(`${API}/voice/transcribe-only`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.transcription;
      }
      return null;
    } catch (error) {
      console.error("Transcription error:", error);
      return null;
    }
  };

  // Play received walkie-talkie / voice message.
  // Toggles: if the same message is already playing → pause it (red).
  // Otherwise stops any other playing audio and starts this one (green while playing).
  const playWalkieTalkieMessage = (voiceUrl, messageId) => {
    if (!voiceUrl) {
      toast.error("Audio no disponible");
      return;
    }

    // If THIS message is already playing → toggle off
    const existing = audioPlayersRef.current[messageId];
    if (existing && playingAudioId === messageId) {
      try { existing.pause(); existing.currentTime = 0; } catch {}
      delete audioPlayersRef.current[messageId];
      setPlayingAudioId(null);
      return;
    }

    // Stop any currently playing audio (we only allow one at a time)
    Object.values(audioPlayersRef.current).forEach(audio => {
      try { audio.pause(); audio.currentTime = 0; } catch (e) { /* noop */ }
    });
    audioPlayersRef.current = {};

    // voiceUrl already includes "/api/voice/..." prefix from backend.
    // Use BACKEND_URL (not API) to avoid double /api/api/ which breaks playback.
    const fullUrl = voiceUrl.startsWith("http") ? voiceUrl : `${BACKEND_URL}${voiceUrl}`;
    const audio = new Audio(fullUrl);
    audio.preload = "auto";
    audioPlayersRef.current[messageId] = audio;

    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => setPlayingAudioId(messageId))
        .catch((e) => {
          console.error("Audio play error:", e, fullUrl);
          toast.error("No se pudo reproducir el audio");
          delete audioPlayersRef.current[messageId];
          setPlayingAudioId(null);
        });
    }

    audio.onended = () => {
      delete audioPlayersRef.current[messageId];
      setPlayingAudioId(prev => (prev === messageId ? null : prev));
    };
    audio.onpause = () => {
      // Triggers when paused programmatically or by browser; reflect in UI
      setPlayingAudioId(prev => (prev === messageId ? null : prev));
    };
    audio.onerror = () => {
      console.error("Audio load error:", fullUrl);
      toast.error("Error al cargar el audio");
      delete audioPlayersRef.current[messageId];
      setPlayingAudioId(prev => (prev === messageId ? null : prev));
    };
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Location sharing
  const getCurrentLocation = () => {
    setLoadingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.error("No se pudo obtener la ubicación");
          setLoadingLocation(false);
        }
      );
    } else {
      toast.error("Geolocalización no soportada");
      setLoadingLocation(false);
    }
  };

  const sendLocation = async () => {
    if (!currentLocation || !selectedConversation) {
      toast.error("No se pudo obtener la ubicación");
      return;
    }
    
    const mapsUrl = `https://www.google.com/maps?q=${currentLocation.lat},${currentLocation.lng}`;
    // Use OpenStreetMap static map (no API key needed)
    const mapPreviewUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${currentLocation.lat},${currentLocation.lng}&zoom=15&size=400x200&markers=${currentLocation.lat},${currentLocation.lng},red-pushpin`;
    
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          content: `📍 Ubicación compartida`,
          message_type: "location",
          file_url: mapsUrl,
          url_preview: {
            url: mapsUrl,
            title: "Mi ubicación",
            description: `Lat: ${currentLocation.lat.toFixed(6)}, Lng: ${currentLocation.lng.toFixed(6)}`,
            image: mapPreviewUrl
          }
        }),
      });
      
      if (response.ok) {
        setShowLocationDialog(false);
        setCurrentLocation(null);
        toast.success("Ubicación compartida");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al compartir ubicación");
      }
    } catch (error) {
      console.error("Error sending location:", error);
      toast.error("Error al compartir ubicación");
    }
  };

  // Check for URL in message
  const checkForUrl = async (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex);
    
    if (urls && urls.length > 0) {
      try {
        const response = await fetch(`${API}/url-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ url: urls[0] }),
        });
        if (response.ok) return await response.json();
      } catch (error) {
        console.error("Error getting URL preview:", error);
      }
    }
    return null;
  };

  // Send message - FIXED to show immediately
  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;

    const content = messageInput.trim();
    setMessageInput("");

    try {
      const urlPreview = await checkForUrl(content);
      const hasUrl = urlPreview && urlPreview.title;

      const messageData = {
        conversation_id: selectedConversation.conversation_id,
        content: content,
        message_type: hasUrl ? "url" : "text",
        url_preview: hasUrl ? urlPreview : null,
      };

      // Add reply_to if replying to a message
      if (replyingTo) {
        messageData.reply_to = replyingTo.message_id;
      }

      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify(messageData),
      });

      if (!response.ok) {
        toast.error("Error al enviar mensaje");
        setMessageInput(content);
      } else {
        setReplyingTo(null); // Clear reply state after sending
      }
      // Message will appear via WebSocket
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar mensaje");
      setMessageInput(content);
    }
  };

  // Send temporary message
  const sendTemporaryMessage = async () => {
    if (!temporaryContent.trim() || !selectedConversation) return;

    try {
      const response = await fetch(`${API}/messages/temporary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          content: temporaryContent,
          expires_in_minutes: temporaryMinutes
        }),
      });

      if (response.ok) {
        toast.success(`Mensaje temporal enviado (expira en ${temporaryMinutes} min)`);
        setTemporaryContent("");
        setShowTemporaryDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar mensaje temporal");
      }
    } catch (error) {
      console.error("Error sending temporary message:", error);
      toast.error("Error al enviar mensaje temporal");
    }
  };

  // Handle reply to message
  const handleReply = (message) => {
    setReplyingTo(message);
    // Focus on message input
    document.querySelector('input[placeholder*="mensaje"]')?.focus();
  };

  // Handle message edit update
  const handleMessageEdit = (messageId, newContent) => {
    setMessages(prev => prev.map(msg =>
      msg.message_id === messageId
        ? { ...msg, content: newContent, edited_at: new Date().toISOString() }
        : msg
    ));
  };

  // Handle pin toggle
  const handlePinToggle = (messageId, isPinned) => {
    setMessages(prev => prev.map(msg =>
      msg.message_id === messageId
        ? { ...msg, is_pinned: isPinned }
        : msg
    ));
  };

  // Handle highlight toggle
  const handleHighlightToggle = (messageId, isHighlighted) => {
    setMessages(prev => prev.map(msg =>
      msg.message_id === messageId
        ? { ...msg, is_highlighted: isHighlighted }
        : msg
    ));
  };

  // Save user to category
  const saveUserToCategory = async (userId, category) => {
    setSavingToCategory(true);
    try {
      const res = await fetch(`${API}/user/saved/${category}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ category, user_id: userId })
      });
      if (res.ok) {
        const categoryNames = {
          contacts: "Contactos",
          stores: "Mis Tiendas",
          restaurants: "Mis Restaurantes",
          chats: "Chats"
        };
        toast.success(`Guardado en ${categoryNames[category]}`);
        setShowUserProfile(false);
      } else {
        toast.error("Error al guardar");
      }
    } catch (err) {
      toast.error("Error al guardar");
    }
    setSavingToCategory(false);
  };

  // Share user profile
  const shareUserProfile = async (user) => {
    const profileUrl = `https://${platformDomain}/u/${user.nickname}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Perfil de ${user.name}`,
          text: `Mira el perfil de ${user.name} en OkChat`,
          url: profileUrl
        });
      } else {
        await navigator.clipboard.writeText(profileUrl);
        toast.success("Enlace copiado al portapapeles");
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(profileUrl);
        toast.success("Enlace copiado al portapapeles");
      }
    }
  };

  // Delete message
  const deleteMessage = async (messageId) => {
    try {
      const response = await fetch(`${API}/messages/${messageId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        // Update locally immediately
        setMessages(prev => prev.map(msg =>
          msg.message_id === messageId
            ? { ...msg, is_deleted: true, content: "Mensaje eliminado" }
            : msg
        ));
        toast.success("Mensaje eliminado");
        setMessageToDelete(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al eliminar mensaje");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Error al eliminar mensaje");
    }
  };

  // Create payment
  const createPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) < 1) {
      toast.error("El monto mínimo es $1.00");
      return;
    }

    if (!selectedConversation || selectedConversation.is_group) {
      toast.error("Solo puedes enviar pagos en chats privados");
      return;
    }

    const recipientId = selectedConversation.participants.find(p => p !== user.user_id);
    if (!recipientId) return;

    setProcessingPayment(true);

    try {
      const response = await fetch(`${API}/payments/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_id: recipientId,
          amount: parseFloat(paymentAmount),
          currency: paymentCurrency,
          message: paymentMessage,
          origin_url: window.location.origin,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear pago");
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error("Error al crear pago");
    } finally {
      setProcessingPayment(false);
      setShowPaymentDialog(false);
      setPaymentAmount("");
      setPaymentMessage("");
    }
  };

  // Handle typing
  const handleTyping = () => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN && selectedConversation) {
      try {
        ws.current.send(JSON.stringify({
          type: "typing",
          conversation_id: selectedConversation.conversation_id,
          is_typing: true
        }));

        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

        typingTimeoutRef.current = setTimeout(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: "typing",
              conversation_id: selectedConversation.conversation_id,
              is_typing: false
            }));
          }
        }, 2000);
      } catch (error) {
        console.warn("WebSocket not ready");
      }
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    // Enter ahora agrega nueva línea, no envía
    // El mensaje se envía solo con el botón de envío
  };

  // Delete view-once message after viewing
  const deleteViewOnceMessage = async (messageId) => {
    try {
      await fetch(`${API}/messages/${messageId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
        credentials: "include",
      });
      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId 
          ? { ...msg, is_deleted: true, content: "Mensaje de una sola vista" }
          : msg
      ));
    } catch (error) {
      console.error("Error deleting view-once message:", error);
    }
  };

  // Block user
  const handleBlockUser = async () => {
    if (!userToBlock) return;
    
    try {
      const response = await fetch(`${API}/user/block`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ blocked_user_id: userToBlock }),
      });

      if (response.ok) {
        toast.success("Usuario bloqueado");
        setSelectedConversation(null);
        setMessages([]);
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al bloquear usuario");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setShowBlockDialog(false);
      setUserToBlock(null);
    }
  };

  // Create conversation
  const createConversation = async (participantId) => {
    try {
      const response = await fetch(`${API}/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ participant_ids: [participantId], is_group: false }),
      });

      if (response.ok) {
        const conv = await response.json();
        setShowNewChatDialog(false);
        setMobileMenuOpen(false);
        fetchConversations();
        // Use window.location for more reliable navigation on mobile
        if (window.innerWidth < 768) {
          window.location.href = `/chat/${conv.conversation_id}`;
        } else {
          navigate(`/chat/${conv.conversation_id}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear conversación");
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast.error("Error al crear conversación");
    }
  };

  // Create group
  const createGroup = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) {
      toast.error("Ingresa un nombre y selecciona participantes");
      return;
    }

    try {
      const response = await fetch(`${API}/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: groupName, participant_ids: selectedUsers, is_group: true }),
      });

      if (response.ok) {
        const conv = await response.json();
        setShowNewGroupDialog(false);
        setGroupName("");
        setSelectedUsers([]);
        setMobileMenuOpen(false);
        fetchConversations();
        navigate(`/chat/${conv.conversation_id}`);
        toast.success("Grupo creado");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error("Error al crear grupo");
    }
  };

  // Add reaction
  const addReaction = async (messageId, emoji) => {
    try {
      await fetch(`${API}/messages/${messageId}/reaction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message_id: messageId, emoji }),
      });
    } catch (error) {
      console.error("Error adding reaction:", error);
    }
  };

  // Insert emoji
  const insertEmoji = (emoji) => {
    setMessageInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Get conversation display name
  const getConversationName = (conv) => {
    if (!conv) return "Usuario";
    if (conv.is_group) return conv.name || "Grupo";
    
    // Try to find the other participant
    const otherParticipant = conv.participant_info?.find(p => p.user_id !== user?.user_id);
    if (otherParticipant?.name) return otherParticipant.name;
    
    // Fallback: if participant_info is empty, try to get from name field
    if (conv.name) return conv.name;
    
    // Last resort: check if there's any participant info at all
    if (conv.participant_info?.length > 0) {
      // If only one participant (might be ourselves), use that
      return conv.participant_info[0]?.name || "Usuario";
    }
    
    return "Chat";
  };

  // Get conversation avatar
  const getConversationAvatar = (conv) => {
    if (!conv) return null;
    if (conv.is_group) return null;
    const otherParticipant = conv.participant_info?.find(p => p.user_id !== user?.user_id);
    return otherParticipant?.picture;
  };

  // Handle logout
  const handleLogout = async () => {
    localStorage.removeItem("okchat_nickname");
    await logout();
    navigate("/");
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const filteredConversations = conversations.filter(conv => {
    const name = getConversationName(conv).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Get currency symbol
  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || "$";
  };

  // Render message content
  const renderMessageContent = (msg, isOwn) => {
    const baseUrl = BACKEND_URL;

    if (msg.is_deleted) {
      return <p className={`italic ${isOwn ? 'text-white/50' : 'text-slate-500'}`}>Mensaje eliminado</p>;
    }

    switch (msg.message_type) {
      case "image":
        // Check if this is a view-once message
        if (msg.is_view_once) {
          const hasViewed = viewedOnceMessages.has(msg.message_id);
          if (hasViewed) {
            return (
              <div className="flex items-center space-x-2 p-3 bg-slate-700/30 rounded-lg">
                <EyeOff className="w-5 h-5 text-slate-500" />
                <span className="text-slate-500 text-sm">Foto vista</span>
              </div>
            );
          }
          return (
            <div 
              className="relative max-w-xs cursor-pointer group"
              onClick={() => {
                setViewOnceMedia({ type: "image", url: `${baseUrl}${msg.file_url}`, messageId: msg.message_id });
                setShowViewOnceViewer(true);
              }}
            >
              <img
                src={`${baseUrl}${msg.file_url}`}
                alt="Foto"
                className="rounded-lg max-w-full blur-xl"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 rounded-lg">
                <Eye className="w-8 h-8 text-white mb-2" />
                <span className="text-white text-sm font-medium">Foto de una sola vista</span>
                <span className="text-white/70 text-xs">Toca para ver</span>
              </div>
            </div>
          );
        }
        return (
          <div className="max-w-xs">
            <img
              src={`${baseUrl}${msg.file_url}`}
              alt={msg.file_name || "Imagen"}
              className="rounded-lg cursor-pointer max-w-full"
              onClick={() => setPreviewFile({ type: "image", url: `${baseUrl}${msg.file_url}`, name: msg.file_name })}
            />
            {msg.caption && (
              <p className={`mt-2 text-sm whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-slate-200'}`}>
                {msg.caption}
              </p>
            )}
          </div>
        );

      case "gif":
        return (
          <div className="max-w-xs">
            <img src={msg.file_url || msg.content} alt="GIF" className="rounded-lg max-w-full" />
            {msg.caption && (
              <p className={`mt-2 text-sm whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-slate-200'}`}>
                {msg.caption}
              </p>
            )}
          </div>
        );

      case "video":
        // Check if this is a view-once video
        if (msg.is_view_once) {
          const hasViewed = viewedOnceMessages.has(msg.message_id);
          if (hasViewed) {
            return (
              <div className="flex items-center space-x-2 p-3 bg-slate-700/30 rounded-lg">
                <EyeOff className="w-5 h-5 text-slate-500" />
                <span className="text-slate-500 text-sm">Video visto</span>
              </div>
            );
          }
          return (
            <div 
              className="relative max-w-xs cursor-pointer group"
              onClick={() => {
                setViewOnceMedia({ type: "video", url: `${baseUrl}${msg.file_url}`, messageId: msg.message_id });
                setShowViewOnceViewer(true);
              }}
            >
              <video src={`${baseUrl}${msg.file_url}`} className="rounded-lg max-w-full blur-xl" preload="metadata" />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 rounded-lg">
                <Eye className="w-8 h-8 text-white mb-2" />
                <span className="text-white text-sm font-medium">Video de una sola vista</span>
                <span className="text-white/70 text-xs">Toca para ver</span>
              </div>
            </div>
          );
        }
        return (
          <div className="max-w-xs">
            <div 
              className="relative rounded-lg overflow-hidden cursor-pointer bg-black"
              onClick={() => setPreviewFile({ type: "video", url: `${baseUrl}${msg.file_url}`, name: msg.file_name })}
            >
              <video src={`${baseUrl}${msg.file_url}`} className="max-w-full" preload="metadata" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-6 h-6 text-slate-900 ml-1" />
                </div>
              </div>
            </div>
            {msg.caption && (
              <p className={`mt-2 text-sm whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-slate-200'}`}>
                {msg.caption}
              </p>
            )}
          </div>
        );

      case "voice": {
        // Render with custom play button so we can switch red/green based on playback state.
        const isPlaying = playingAudioId === msg.message_id;
        const audioUrl = msg.voice_url || msg.file_url;
        return (
          <div className="space-y-2 max-w-[280px]">
            <div className={`flex items-center space-x-3 p-3 rounded-xl ${isOwn ? 'bg-white/10' : 'bg-slate-700/30'}`}>
              <button
                onClick={() => playWalkieTalkieMessage(audioUrl, msg.message_id)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-105 shadow-lg ${
                  isPlaying
                    ? "bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 ring-2 ring-green-400/50"
                    : "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-400 hover:to-red-500"
                }`}
                aria-label={isPlaying ? "Pausar audio" : "Reproducir audio"}
              >
                {isPlaying
                  ? <Pause className="w-6 h-6 text-white" />
                  : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Mic className={`w-4 h-4 ${isOwn ? 'text-white/70' : 'text-slate-400'}`} />
                  <span className={`text-xs font-medium ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
                    Nota de voz
                  </span>
                </div>
                {msg.voice_duration && (
                  <p className={`text-xs mt-1 ${isOwn ? 'text-white/50' : 'text-slate-500'}`}>
                    {formatTime(Math.round(msg.voice_duration))}
                  </p>
                )}
              </div>
            </div>
            {msg.transcription && (
              <div className="px-3 py-2 rounded-lg text-sm bg-red-600 text-white shadow-md">
                <p className="italic">"{msg.transcription}"</p>
              </div>
            )}
          </div>
        );
      }

      case "normal": {
        // Normal audio message (recorded with the regular mic button)
        const isPlaying = playingAudioId === msg.message_id;
        return (
          <div className="space-y-2 max-w-[280px]">
            <div className={`flex items-center space-x-3 p-3 rounded-xl ${isOwn ? 'bg-gradient-to-r from-slate-600 to-slate-700' : 'bg-gradient-to-r from-slate-700 to-slate-800'} border ${isOwn ? 'border-slate-500/30' : 'border-slate-600/30'}`}>
              <button
                onClick={() => playWalkieTalkieMessage(msg.voice_url, msg.message_id)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-105 shadow-lg ${
                  isPlaying
                    ? "bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 ring-2 ring-green-400/50"
                    : "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-400 hover:to-red-500"
                }`}
                aria-label={isPlaying ? "Pausar audio" : "Reproducir audio"}
              >
                {isPlaying
                  ? <Pause className="w-6 h-6 text-white" />
                  : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Mic className={`w-4 h-4 ${isOwn ? 'text-white/70' : 'text-slate-400'}`} />
                  <span className={`text-xs font-medium ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
                    Nota de voz
                  </span>
                </div>
                {msg.voice_duration && (
                  <p className={`text-xs mt-1 ${isOwn ? 'text-white/50' : 'text-slate-500'}`}>
                    {formatTime(Math.round(msg.voice_duration))}
                  </p>
                )}
              </div>
            </div>
            {msg.transcription && (
              <div className="px-3 py-2 rounded-lg text-sm bg-red-600 text-white shadow-md">
                <p className="italic">"{msg.transcription}"</p>
              </div>
            )}
          </div>
        );
      }

      case "walkie_talkie": {
        const isPlaying = playingAudioId === msg.message_id;
        return (
          <div className="space-y-2 max-w-[280px]">
            <div className={`flex items-center space-x-3 p-3 rounded-xl ${isOwn ? 'bg-gradient-to-r from-red-600 to-red-700' : 'bg-gradient-to-r from-slate-700 to-slate-800'} border ${isOwn ? 'border-red-500/30' : 'border-slate-600/30'}`}>
              <button
                onClick={() => playWalkieTalkieMessage(msg.voice_url, msg.message_id)}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-105 shadow-lg ${
                  isPlaying
                    ? "bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 ring-2 ring-green-400/50"
                    : "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-400 hover:to-red-500"
                }`}
                aria-label={isPlaying ? "Pausar audio" : "Reproducir audio"}
              >
                {isPlaying
                  ? <Pause className="w-6 h-6 text-white" />
                  : <Play className="w-6 h-6 text-white ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Radio className={`w-4 h-4 ${isOwn ? 'text-white/70' : 'text-red-400'}`} />
                  <span className={`text-xs font-medium ${isOwn ? 'text-white/70' : 'text-slate-400'}`}>
                    Walkie-Talkie
                  </span>
                </div>
                {msg.voice_duration && (
                  <p className={`text-xs mt-1 ${isOwn ? 'text-white/50' : 'text-slate-500'}`}>
                    {formatTime(Math.round(msg.voice_duration))}
                  </p>
                )}
              </div>
            </div>
            {msg.transcription && (
              <div className="px-3 py-2 rounded-lg text-sm bg-red-600 text-white shadow-md font-medium">
                <p className="italic">"{msg.transcription}"</p>
              </div>
            )}
          </div>
        );
      }

      case "product": {
        const snap = msg.product_snapshot || {};
        const isMine = msg.sender_id === user?.user_id;
        const handleBuy = async () => {
          try {
            const res = await fetch(`${API}/products/${snap.product_id || msg.product_id}/checkout`, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...(localStorage.getItem('session_token') ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` } : {}) },
              credentials: "include",
              body: JSON.stringify({
                conversation_id: msg.conversation_id,
                quantity: 1,
                origin_url: window.location.origin,
              }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.detail || "No se pudo iniciar el pago");
            }
            const data = await res.json();
            if (data.checkout_url) window.location.href = data.checkout_url;
          } catch (e) {
            toast.error(e.message);
          }
        };
        return (
          <div className="max-w-[280px] rounded-xl overflow-hidden border border-emerald-500/30 bg-gradient-to-br from-slate-800 to-slate-900">
            {snap.image_url && (
              <img src={snap.image_url} alt={snap.name} className="w-full h-36 object-cover" />
            )}
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-medium text-emerald-300 uppercase tracking-wide">Producto</span>
              </div>
              <p className="text-white font-semibold leading-tight">{snap.name || "Producto"}</p>
              {snap.description && (
                <p className="text-xs text-slate-300 line-clamp-3">{snap.description}</p>
              )}
              <p className="text-emerald-400 font-bold text-lg">
                {(snap.currency || "MXN")} {Number(snap.price || 0).toFixed(2)}
              </p>
              {!isMine && (
                <button
                  onClick={handleBuy}
                  className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors"
                >
                  Pagar ahora
                </button>
              )}
              {isMine && (
                <p className="text-xs text-slate-500 italic">Esperando que el comprador pague…</p>
              )}
            </div>
          </div>
        );
      }

      case "sticker": {
        const snap = msg.sticker_snapshot || {};
        const url = snap.image_url || msg.file_url;
        return (
          <div className="max-w-[200px]">
            <img src={url} alt={snap.name || "sticker"} className="w-44 h-44 object-contain drop-shadow-lg" />
          </div>
        );
      }

      case "mini_app": {
        const snap = msg.mini_app_snapshot || {};
        const url = snap.url || msg.file_url;
        return (
          <div className="max-w-[320px] rounded-xl overflow-hidden border border-purple-500/30 bg-gradient-to-br from-slate-800 to-slate-900">
            <div className="p-3 flex items-center gap-2">
              <span className="text-2xl">{snap.icon || "📲"}</span>
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold truncate">{snap.name || msg.file_name || "Mini-app"}</p>
                {snap.description && (
                  <p className="text-xs text-slate-400 truncate">{snap.description}</p>
                )}
              </div>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 pb-3 text-xs text-purple-300 hover:text-purple-200"
            >
              <ExternalLink className="w-3 h-3 inline mr-1" /> Abrir en nueva pestaña
            </a>
            <iframe
              src={url}
              title={snap.name || "mini-app"}
              sandbox={snap.sandbox || "allow-scripts allow-same-origin"}
              className="w-full h-64 bg-white"
              loading="lazy"
            />
          </div>
        );
      }

      case "product_paid": {
        const snap = msg.product_snapshot || {};
        return (
          <div className="max-w-[280px] rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-1">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-medium text-emerald-300">Pago confirmado</span>
            </div>
            <p className="text-white font-semibold">{snap.name || "Producto"}</p>
            <p className="text-emerald-300 text-sm">
              {(snap.currency || "MXN").toUpperCase()} {Number(snap.price || 0).toFixed(2)}
            </p>
          </div>
        );
      }

      case "document":
        return (
          <div className="max-w-xs">
            <a
              href={`${baseUrl}${msg.file_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center space-x-3 p-3 rounded-lg ${isOwn ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-700/30 hover:bg-slate-700/50'} transition-colors`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOwn ? 'bg-white/20' : 'bg-red-500/30'}`}>
                <FileText className={`w-5 h-5 ${isOwn ? 'text-white' : 'text-red-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-white">{msg.file_name || "Documento"}</p>
                <p className="text-xs text-slate-400">{formatFileSize(msg.file_size)}</p>
              </div>
              <Download className="w-5 h-5 text-slate-400" />
            </a>
            {msg.caption && (
              <p className={`mt-2 text-sm whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-slate-200'}`}>
                {msg.caption}
              </p>
            )}
          </div>
        );

      case "location":
        return (
          <a
            href={msg.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`block rounded-lg overflow-hidden border ${isOwn ? 'border-white/20' : 'border-slate-600'}`}
          >
            {msg.url_preview?.image && (
              <img src={msg.url_preview.image} alt="Mapa" className="w-full h-32 object-cover" />
            )}
            <div className={`p-3 ${isOwn ? 'bg-white/10' : 'bg-slate-700/50'}`}>
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-red-400" />
                <span className="text-white font-medium text-sm">{msg.url_preview?.title || "Ubicación"}</span>
              </div>
              {msg.url_preview?.description && (
                <p className="text-xs text-slate-400 mt-1">{msg.url_preview.description}</p>
              )}
            </div>
          </a>
        );

      case "url":
        // Check if it's a YouTube video
        const youtubeVideoId = msg.url_preview?.video_id || (() => {
          const patterns = [
            /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
            /youtu\.be\/([a-zA-Z0-9_-]{11})/,
            /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
          ];
          for (const pattern of patterns) {
            const match = msg.content?.match(pattern);
            if (match) return match[1];
          }
          return null;
        })();
        
        if (youtubeVideoId || msg.url_preview?.type === "youtube") {
          const videoId = youtubeVideoId || msg.url_preview?.video_id;
          return (
            <div className="space-y-2">
              <p className="text-white">{msg.content}</p>
              <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          );
        }
        
        // Check for Vimeo
        if (msg.url_preview?.type === "vimeo") {
          return (
            <div className="space-y-2">
              <p className="text-white">{msg.content}</p>
              <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={msg.url_preview.embed_url}
                  title="Vimeo video"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          );
        }
        
        // Regular URL preview
        {
          const translatedUrl = messageTranslations[msg.message_id];
          const isOtherUrl = msg.sender_id !== user?.user_id;
          return (
            <div>
              <p className="text-white whitespace-pre-wrap break-words">{msg.content}</p>
              {translatedUrl && isOtherUrl && translatedUrl !== msg.content && (
                <div className="mt-2 px-2 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-xs">
                  <p className="text-blue-300/80 font-medium mb-0.5 flex items-center gap-1">
                    <Languages className="w-3 h-3" /> Traducción
                  </p>
                  <p className="text-blue-100 italic">{translatedUrl}</p>
                </div>
              )}
              {msg.url_preview && msg.url_preview.title && (
                <a
                  href={msg.url_preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-2 block rounded-lg overflow-hidden border ${isOwn ? 'border-white/20 bg-white/10' : 'border-slate-600 bg-slate-700/50'} hover:opacity-90 transition-opacity`}
                >
                  {msg.url_preview.image && <img src={msg.url_preview.image} alt="" className="w-full h-32 object-cover" />}
                  <div className="p-3">
                    <p className="font-medium text-sm text-white">{msg.url_preview.title}</p>
                    {msg.url_preview.description && <p className="text-xs mt-1 line-clamp-2 text-slate-400">{msg.url_preview.description}</p>}
                    <p className="text-xs mt-1 text-slate-500">{new URL(msg.url_preview.url).hostname}</p>
                  </div>
                </a>
              )}
            </div>
          );
        }

      case "payment":
        return (
          <div className={`flex items-center space-x-3 p-3 rounded-lg ${isOwn ? 'bg-green-500/20' : 'bg-green-500/10'}`}>
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-green-400 font-medium">
                {getCurrencySymbol(msg.payment_info?.currency)}{msg.payment_info?.amount?.toFixed(2)} {msg.payment_info?.currency?.toUpperCase()}
              </p>
              <p className="text-xs text-slate-400">Pago completado</p>
            </div>
          </div>
        );

      default: {
        const translated = messageTranslations[msg.message_id];
        const isOther = msg.sender_id !== user?.user_id;
        return (
          <div>
            <p className="text-white whitespace-pre-wrap break-words">{msg.content}</p>
            {translated && isOther && translated !== msg.content && (
              <div className="mt-2 px-2 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-xs">
                <p className="text-blue-300/80 font-medium mb-0.5 flex items-center gap-1">
                  <Languages className="w-3 h-3" /> Traducción
                </p>
                <p className="text-blue-100 italic">{translated}</p>
              </div>
            )}
          </div>
        );
      }
    }
  };

  // Sidebar content (reusable for desktop and mobile)
  const SidebarContent = ({ onClose }) => (
    <>
      <div className="p-4 border-b border-[#0e1621]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <img src="/icono-logo-okchat.png" alt="OkChat" className="w-10 h-10 object-contain rounded-full" />
            <span className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>OkChat</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-[#232e3c] px-3">
                <Menu className="w-4 h-4 mr-2" />
                <span className="text-sm">Menú</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#232e3c] border-[#0e1621] w-64">
              {/* OkGo Search Bar */}
              <div className="p-2 border-b border-[#0e1621]">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <ExternalLink className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                    <Input 
                      type="text"
                      placeholder="Buscar en OkGo..." 
                      value={okgoSearchQuery} 
                      onChange={(e) => setOkgoSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && okgoSearchQuery.trim()) {
                          e.preventDefault();
                          window.open(`https://app.okgo.mx/search?q=${encodeURIComponent(okgoSearchQuery.trim())}`, '_blank');
                        }
                      }}
                      className="pl-7 h-8 text-xs bg-[#1a2332] border-transparent text-white placeholder:text-slate-500" 
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (okgoSearchQuery.trim()) {
                        window.open(`https://app.okgo.mx/search?q=${encodeURIComponent(okgoSearchQuery.trim())}`, '_blank');
                      }
                    }}
                    disabled={!okgoSearchQuery.trim()}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold px-3 h-8 text-xs"
                  >
                    GO
                  </Button>
                </div>
              </div>
              <DropdownMenuItem onClick={() => { setShowNewChatDialog(true); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <UserPlus className="w-4 h-4 mr-2" /> Nuevo chat
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowNewGroupDialog(true); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <Users className="w-4 h-4 mr-2" /> Nuevo grupo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowSearchDialog(true); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <Search className="w-4 h-4 mr-2" /> Búsqueda global
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowPaymentHistory(true); fetchPaymentHistory(); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <History className="w-4 h-4 mr-2" /> Historial de pagos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowPremiumDialog(true); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <BadgeCheck className="w-4 h-4 mr-2 text-blue-400" />
                {user?.subscription_tier === "business" ? "Mi membresía Business" : "OkChat for Business"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowGamificationDialog(true); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <Trophy className="w-4 h-4 mr-2 text-amber-400" /> Mi progreso
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowDevicesDialog(true); onClose?.(); }} className="text-white hover:bg-[#2b5278]">
                <Smartphone className="w-4 h-4 mr-2 text-blue-300" /> Mis dispositivos
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#0e1621]" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 hover:bg-[#2b5278]">
                <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Buscar usuario..."
            value={sidebarSearchQuery}
            onChange={(e) => setSidebarSearchQuery(e.target.value)}
            className="pl-10 bg-[#242f3d] border-transparent text-white placeholder:text-slate-500 focus:bg-[#2b3a4c] focus:border-red-500"
          />
          {sidebarSearchQuery && (
            <button
              onClick={() => setSidebarSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Resultados de búsqueda del sidebar */}
        {sidebarSearchQuery.trim().length >= 2 && (
          <div className="absolute top-full left-0 right-0 bg-[#17212b] border border-[#0e1621] rounded-b-lg shadow-xl z-50 max-h-64 overflow-y-auto">
            <div className="p-2">
              <p className="text-xs text-slate-500 px-2 mb-2">Resultados de búsqueda</p>
              {sidebarSearchResults.length > 0 ? (
                sidebarSearchResults.map(u => (
                  <div
                    key={u.user_id}
                    onClick={async () => {
                      try {
                        const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
                        const response = await fetch(`${API}/conversations`, {
                          method: "POST",
                          headers,
                          credentials: "include",
                          body: JSON.stringify({ participant_ids: [u.user_id], is_group: false }),
                        });
                        if (response.ok) {
                          const conv = await response.json();
                          setSelectedConversation(conv);
                          fetchMessages(conv.conversation_id);
                          fetchConversations();
                          setSidebarSearchQuery("");
                          onClose?.();
                          toast.success("Chat iniciado");
                        }
                      } catch (err) {
                        toast.error("Error al iniciar chat");
                      }
                    }}
                    className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-[#232e3c]"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">
                        {u.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-slate-400 truncate">@{u.nickname}</p>
                    </div>
                    <MessageSquare className="w-4 h-4 text-red-400" />
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">No se encontraron usuarios</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab navigation for Chats vs Sections */}
      <div className="flex border-b border-[#0e1621]">
        <button
          onClick={() => setShowSectionsPanel(false)}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            !showSectionsPanel
              ? "text-red-400 border-b-2 border-red-400"
              : "text-slate-500 hover:text-slate-300"
          }`}
        >
          <MessageSquare className="w-4 h-4 inline mr-2" />
          Chats
        </button>
        <button
          onClick={() => setShowSectionsPanel(true)}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            showSectionsPanel
              ? "text-red-400 border-b-2 border-red-400"
              : "text-slate-500 hover:text-slate-300"
          }`}
        >
          <FolderOpen className="w-4 h-4 inline mr-2" />
          Guardados
        </button>
      </div>

      {/* Conditional rendering of chats or sections panel */}
      {showSectionsPanel ? (
        <div className="flex-1 overflow-hidden">
          <SidebarSections 
            onSelectUser={(user) => {
              // Open public profile or start chat
              if (user.nickname) {
                navigate(`/u/${user.nickname}`);
              }
              onClose?.();
            }}
            onStartChat={(userId) => {
              createConversation(userId);
              onClose?.();
            }}
            platformDomain={platformDomain}
          />
        </div>
      ) : (
      <ScrollArea className="flex-1">
        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="border-b border-[#0e1621]">
            <div className="px-4 py-2 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-xs text-amber-400 font-medium uppercase tracking-wider">Favoritos</span>
            </div>
            <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
              {favorites.map((fav) => (
                <button
                  key={fav.user_id}
                  onClick={() => {
                    // Find or create conversation with this user
                    const existingConv = conversations.find(c => 
                      !c.is_group && c.participants?.includes(fav.user_id)
                    );
                    if (existingConv) {
                      setSelectedConversation(existingConv);
                      fetchMessages(existingConv.conversation_id);
                      navigate(`/chat/${existingConv.conversation_id}`);
                    } else {
                      // Start new chat
                      handleCreateConversation([fav.user_id]);
                    }
                    onClose?.();
                  }}
                  className="flex flex-col items-center gap-1 min-w-[60px] p-2 rounded-lg hover:bg-[#202b36] transition-colors"
                >
                  <div className="relative">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={fav.picture ? `${BACKEND_URL}${fav.picture}` : null} />
                      <AvatarFallback className="bg-gradient-to-br from-amber-500 to-orange-600 text-white text-sm">
                        {fav.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {fav.status === 'online' && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#17212b]" />
                    )}
                  </div>
                  <span className="text-xs text-slate-400 truncate w-full text-center">{fav.name?.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Conversations List */}
        <div className="py-2">
          {filteredConversations.map((conv) => (
            <div
              key={conv.conversation_id}
              className={`flex items-center px-4 py-3 cursor-pointer hover:bg-[#202b36] group ${
                selectedConversation?.conversation_id === conv.conversation_id ? 'bg-[#2b5278]' : ''
              }`}
            >
              <div 
                className="relative"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!conv.is_group) {
                    const otherUser = conv.participant_info?.find(p => p.user_id !== user?.user_id) || conv.participant_info?.[0];
                    if (otherUser) {
                      setSelectedUserProfile(otherUser);
                      setShowUserProfile(true);
                    }
                  }
                }}
              >
                <Avatar className="w-12 h-12 hover:ring-2 hover:ring-red-500/50 transition-all">
                  <AvatarImage src={getConversationAvatar(conv)} />
                  <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">
                    {conv.is_group ? <Hash className="w-5 h-5" /> : getConversationName(conv).charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {!conv.is_group && conv.participant_info?.find(p => p.user_id !== user?.user_id)?.status === 'online' && (
                  <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-[#17212b]"></div>
                )}
              </div>
              <div 
                className="ml-3 flex-1 min-w-0"
                onClick={() => {
                  setSelectedConversation(conv);
                  fetchMessages(conv.conversation_id);
                  onClose?.();
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-white truncate">{getConversationName(conv)}</span>
                  {conv.last_message && (
                    <span className="text-xs text-slate-500">
                      {new Date(conv.last_message.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400 truncate">
                    {typingUsers[conv.conversation_id] ? (
                      <span className="text-red-400 flex items-center">
                        <span className="flex space-x-0.5 mr-1.5">
                          <span className="w-1.5 h-1.5 bg-red-400 rounded-full typing-dot"></span>
                          <span className="w-1.5 h-1.5 bg-red-400 rounded-full typing-dot"></span>
                          <span className="w-1.5 h-1.5 bg-red-400 rounded-full typing-dot"></span>
                        </span>
                        escribiendo...
                      </span>
                    ) : (
                      conv.last_message?.content || <span className="italic text-slate-500">Sin mensajes</span>
                    )}
                  </p>
                  {conv.unread_count > 0 && (
                    <span className="bg-red-600 text-white text-xs font-medium px-2 py-0.5 rounded-full ml-2">{conv.unread_count}</span>
                  )}
                </div>
              </div>
              
              {/* Menu de opciones */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    onClick={(e) => e.stopPropagation()}
                    className="ml-2 p-2 rounded-full hover:bg-[#2b5278] opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="w-4 h-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#232e3c] border-[#0e1621]">
                  <DropdownMenuItem 
                    className="text-white hover:bg-[#2b5278] cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Anclar conversación
                      toast.success("Conversación anclada");
                    }}
                  >
                    <Pin className="w-4 h-4 mr-2" />
                    Anclar chat
                  </DropdownMenuItem>
                  {!conv.is_group && (
                    <DropdownMenuItem 
                      className="text-white hover:bg-[#2b5278] cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const otherUser = conv.participant_info?.find(p => p.user_id !== user?.user_id);
                        if (otherUser) {
                          saveUserToCategory(otherUser.user_id, "contacts");
                        }
                      }}
                    >
                      <Bookmark className="w-4 h-4 mr-2" />
                      Guardar contacto
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    className="text-white hover:bg-[#2b5278] cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Archivar/cerrar conversación
                      toast.success("Conversación archivada");
                    }}
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archivar chat
                  </DropdownMenuItem>
                  {!conv.is_group && (
                    <DropdownMenuItem 
                      className="text-red-400 hover:bg-red-500/20 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const otherUser = conv.participant_info?.find(p => p.user_id !== user?.user_id);
                        if (otherUser) {
                          // Bloquear usuario
                          toast.success("Usuario bloqueado");
                        }
                      }}
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Bloquear usuario
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </ScrollArea>
      )}

      <div className="p-4 border-t border-[#0e1621]">
        <div 
          className="flex items-center space-x-3 cursor-pointer hover:bg-[#202b36] -mx-4 px-4 py-2 transition-colors"
          onClick={() => navigate('/profile')}
        >
          <Avatar className="w-10 h-10">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
              {user?.name?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="h-[100dvh] flex bg-[#0e1621]" style={{ height: '100dvh', minHeight: '-webkit-fill-available' }}>
      {/* New Message Popup Notification */}
      {newMessagePopup && (
        <NewMessagePopup
          message={newMessagePopup.message}
          senderName={newMessagePopup.senderName}
          senderPicture={newMessagePopup.senderPicture}
          onOpen={() => {
            // Buscar la conversación y seleccionarla
            const conv = conversations.find(c => c.conversation_id === newMessagePopup.conversationId);
            if (conv) {
              setSelectedConversation(conv);
              fetchMessages(conv.conversation_id);
            }
            setNewMessagePopup(null);
          }}
          onDismiss={() => setNewMessagePopup(null)}
          autoHideMs={6000}
        />
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:flex w-80 bg-[#17212b] border-r border-[#0e1621] flex-col">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-80 p-0 bg-[#17212b] border-[#0e1621]">
          <div className="h-full flex flex-col">
            <SidebarContent onClose={() => setMobileMenuOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-[#0e1621]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23172231' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="h-14 bg-[#17212b] border-b border-[#0e1621] flex items-center justify-between px-4">
              <div className="flex items-center space-x-3">
                <Button variant="ghost" size="icon" className="md:hidden text-slate-400 hover:text-white" onClick={() => setMobileMenuOpen(true)}>
                  <Menu className="w-5 h-5" />
                </Button>
                <Avatar 
                  className="w-10 h-10 cursor-pointer hover:ring-2 hover:ring-red-500/50 transition-all"
                  onClick={() => {
                    if (!selectedConversation.is_group) {
                      const otherUser = selectedConversation.participant_info?.find(p => p.user_id !== user?.user_id) || selectedConversation.participant_info?.[0];
                      if (otherUser) {
                        setSelectedUserProfile(otherUser);
                        setShowUserProfile(true);
                      }
                    }
                  }}
                >
                  <AvatarImage src={getConversationAvatar(selectedConversation)} />
                  <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">
                    {selectedConversation.is_group ? <Hash className="w-5 h-5" /> : getConversationName(selectedConversation).charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div 
                  className="cursor-pointer"
                  onClick={() => {
                    if (!selectedConversation.is_group) {
                      const otherUser = selectedConversation.participant_info?.find(p => p.user_id !== user?.user_id) || selectedConversation.participant_info?.[0];
                      if (otherUser) {
                        setSelectedUserProfile(otherUser);
                        setShowUserProfile(true);
                      }
                    }
                  }}
                >
                  <h2 className="font-semibold text-white hover:text-red-400 transition-colors flex items-center gap-1">
                    {getConversationName(selectedConversation)}
                    {!selectedConversation.is_group && selectedConversation.participant_info?.find(p => p.user_id !== user?.user_id)?.is_verified && (
                      <VerifiedBadge size={16} />
                    )}
                  </h2>
                  {typingUsers[selectedConversation.conversation_id] ? (
                    <p className="text-xs text-red-400">escribiendo...</p>
                  ) : (
                    <p className="text-xs text-slate-500">
                      {selectedConversation.is_group 
                        ? `${selectedConversation.participants?.length || 0} miembros`
                        : (() => {
                            const otherUser = selectedConversation.participant_info?.find(p => p.user_id !== user?.user_id);
                            return formatLastSeen(otherUser?.last_seen, otherUser?.status);
                          })()
                      }
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-amber-400 hover:text-amber-300 hover:bg-[#232e3c]"
                  onClick={() => setShowSummaryDialog(true)}
                  title="Resumen IA"
                >
                  <Sparkles className="w-5 h-5" />
                </Button>
                {!selectedConversation.is_group && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-blue-400 hover:text-blue-300 hover:bg-[#232e3c]"
                      onClick={() => {
                        const otherId = selectedConversation.participants?.find((p) => p !== user?.user_id);
                        if (!otherId) return;
                        const otherInfo = selectedConversation.participant_info?.find((p) => p.user_id === otherId) || { user_id: otherId, name: "Usuario" };
                        setActiveCall({
                          mode: "outgoing",
                          callId: `call_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
                          peer: { user_id: otherId, name: otherInfo.name, picture: otherInfo.picture },
                        });
                      }}
                      title="Llamada de voz"
                    >
                      <Phone className="w-5 h-5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-green-400 hover:text-green-300 hover:bg-[#232e3c]"
                      onClick={() => setShowPaymentDialog(true)}
                    >
                      <CreditCard className="w-5 h-5" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="w-8 h-8 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-colors">
                          <Plus className="w-5 h-5 text-white" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#232e3c] border-[#0e1621] text-white">
                        <DropdownMenuItem 
                          className="hover:bg-[#2b5278] cursor-pointer"
                          onClick={() => setShowChatSearch(true)}
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Buscar en chat
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="hover:bg-[#2b5278] cursor-pointer text-amber-400 hover:text-amber-300"
                          onClick={() => {
                            const otherUserId = selectedConversation.participants?.find(p => p !== user?.user_id);
                            const isFav = favorites.some(f => f.user_id === otherUserId);
                            toggleFavorite(otherUserId, isFav);
                          }}
                        >
                          {favorites.some(f => f.user_id === selectedConversation.participants?.find(p => p !== user?.user_id)) ? (
                            <>
                              <StarOff className="w-4 h-4 mr-2" />
                              Quitar de favoritos
                            </>
                          ) : (
                            <>
                              <Star className="w-4 h-4 mr-2" />
                              Agregar a favoritos
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-[#0e1621]" />
                        <DropdownMenuItem 
                          className="hover:bg-[#2b5278] cursor-pointer text-red-400 hover:text-red-300"
                          onClick={() => {
                            const otherUserId = selectedConversation.participants?.find(p => p !== user?.user_id);
                            setUserToBlock(otherUserId);
                            setShowBlockDialog(true);
                          }}
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          Bloquear usuario
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
                <Sheet open={showSharedMedia} onOpenChange={setShowSharedMedia}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-[#232e3c]">
                      <Info className="w-5 h-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-80 p-0 bg-[#17212b] border-[#0e1621]">
                    <SharedMediaPanel conversationId={selectedConversation?.conversation_id} />
                  </SheetContent>
                </Sheet>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-1 max-w-3xl mx-auto">
                {/* Load more messages button */}
                {messagesHasMore && (
                  <div className="flex justify-center py-2">
                    <button
                      onClick={() => fetchMessages(selectedConversation.conversation_id, true)}
                      disabled={loadingMoreMessages}
                      className="text-sm text-slate-400 hover:text-white px-4 py-2 rounded-lg bg-[#1e2a36] hover:bg-[#2b3a4c] transition-colors disabled:opacity-50"
                    >
                      {loadingMoreMessages ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Cargando...
                        </span>
                      ) : (
                        'Cargar mensajes anteriores'
                      )}
                    </button>
                  </div>
                )}
                
                {messages.filter(m => m.conversation_id === selectedConversation.conversation_id).map((msg, index, arr) => {
                  const isOwn = msg.sender_id === user?.user_id;
                  const showAvatar = !isOwn && (index === 0 || arr[index - 1]?.sender_id !== msg.sender_id);
                  const showName = !isOwn && selectedConversation.is_group && showAvatar;
                  const isTemporary = msg.is_temporary;
                  const isPinned = msg.is_pinned;
                  const isHighlighted = highlightedMessageId === msg.message_id;
                  
                  return (
                    <div
                      key={msg.message_id}
                      data-message-id={msg.message_id}
                      className={`flex items-end space-x-2 message-animate ${isOwn ? 'justify-end' : ''} ${isPinned ? 'pinned-message pl-2' : ''} ${isTemporary ? 'temporary-message' : ''} ${isHighlighted ? 'highlight-animation' : ''}`}
                    >
                      {!isOwn && showAvatar && (
                        <Avatar 
                          className="w-8 h-8 mb-5 cursor-pointer hover:ring-2 hover:ring-red-500/50"
                          onClick={() => {
                            // Show user profile dialog
                            const otherUser = selectedConversation.participant_info?.find(p => p.user_id === msg.sender_id);
                            if (otherUser) {
                              setSelectedUserProfile({
                                ...otherUser,
                                ...msg.sender_info
                              });
                              setShowUserProfile(true);
                            }
                          }}
                        >
                          <AvatarImage src={msg.sender_info?.picture} />
                          <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-xs">
                            {msg.sender_info?.name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {!isOwn && !showAvatar && <div className="w-8" />}
                      
                      <div className={`group max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                        {showName && <span className="text-xs text-red-400 mb-1 ml-1 font-medium">{msg.sender_info?.name}</span>}
                        
                        {/* Reply preview if message is a reply */}
                        {msg.reply_to && (
                          <div className="reply-preview text-xs text-slate-400 mb-1 cursor-pointer" onClick={() => scrollToMessage(msg.reply_to)}>
                            <Reply className="w-3 h-3 inline mr-1" />
                            Respuesta a un mensaje
                          </div>
                        )}
                        
                        <div className={`relative px-3 py-2 rounded-xl ${isOwn ? 'bg-[#2b5278] rounded-br-sm' : 'bg-[#182533] rounded-bl-sm'}`}>
                          {/* Pinned indicator */}
                          {isPinned && (
                            <div className="absolute -top-3 left-2 flex items-center text-xs text-red-400">
                              <Pin className="w-3 h-3 mr-1" />
                              Fijado
                            </div>
                          )}
                          
                          {renderMessageContent(msg, isOwn)}
                          
                          {/* Reactions */}
                          {Object.keys(msg.reactions || {}).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {Object.entries(msg.reactions).map(([emoji, users]) => (
                                <button
                                  key={emoji}
                                  onClick={() => addReaction(msg.message_id, emoji)}
                                  className={`text-xs px-2 py-0.5 rounded-full ${users.includes(user?.user_id) ? 'bg-red-500/30 text-red-300' : 'bg-slate-700/50 text-slate-400'}`}
                                >
                                  {emoji} {users.length}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Message actions - Now using MessageActionMenu */}
                          <div className={`absolute ${isOwn ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity px-2 flex space-x-1`}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 rounded-full bg-[#232e3c] shadow-md hover:bg-[#2b3a4c]">
                                  <Smile className="w-4 h-4 text-slate-400" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#232e3c] border-[#0e1621]">
                                <div className="grid grid-cols-5 gap-1 p-2">
                                  {EMOJI_LIST.slice(0, 20).map(emoji => (
                                    <button key={emoji} onClick={() => addReaction(msg.message_id, emoji)} className="p-1 hover:bg-[#2b5278] rounded text-lg">
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            
                            {/* New MessageActionMenu component */}
                            <MessageActionMenu
                              message={msg}
                              isOwn={isOwn}
                              onReply={handleReply}
                              onEdit={handleMessageEdit}
                              onPin={handlePinToggle}
                              onHighlight={handleHighlightToggle}
                              conversations={conversations}
                            />
                            
                            {isOwn && !msg.is_deleted && (
                              <button className="p-1.5 rounded-full bg-[#232e3c] shadow-md hover:bg-red-500/30" onClick={() => setMessageToDelete(msg.message_id)}>
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-1 mt-0.5 text-xs text-slate-500 px-1">
                          <span>{new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                          {msg.edited_at && <span className="italic">(editado)</span>}
                          {isTemporary && <TemporaryMessageTimer expiresAt={msg.expires_at} />}
                          {isOwn && (() => {
                            // WhatsApp-style 3-state ticks for own messages:
                            //  ✓  (1 gray)         → sent (server received it, no recipient yet)
                            //  ✓✓ (2 gray)         → delivered to at least one recipient device
                            //  ✓✓ (2 green)        → at least one recipient has read it
                            // We compare against the sender's own user_id to ignore the
                            // server-side convention of including the sender in both arrays.
                            const others = (arr) => (arr || []).filter(u => u !== user?.user_id);
                            const readByOthers = others(msg.read_by).length > 0;
                            const deliveredToOthers = others(msg.delivered_to).length > 0;
                            if (readByOthers) return <CheckCheck className="w-4 h-4 text-green-500" title="Leído" />;
                            if (deliveredToOthers) return <CheckCheck className="w-4 h-4 text-slate-400" title="Entregado" />;
                            return <Check className="w-4 h-4 text-slate-400" title="Enviado" />;
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Typing indicator */}
            {typingUsers[selectedConversation.conversation_id] && (
              <div className="px-4 py-2 max-w-3xl mx-auto w-full">
                <div className="flex items-center space-x-2 text-slate-400 text-sm">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-red-400 rounded-full typing-dot"></div>
                    <div className="w-2 h-2 bg-red-400 rounded-full typing-dot"></div>
                    <div className="w-2 h-2 bg-red-400 rounded-full typing-dot"></div>
                  </div>
                  <span className="text-red-400">
                    {getConversationName(selectedConversation)} está escribiendo...
                  </span>
                </div>
              </div>
            )}

            {/* Voice Recording UI - SOLO para audio normal (tab "chat"). Para Walkie-Talkie
                el botón toggle vive en el panel inferior y maneja él mismo el estado de grabación,
                así que NO debe ocultarse mientras se graba (por eso este overlay está condicionado
                a activeInputTab !== "walkie_talkie"). */}
            {isRecording && activeInputTab !== "walkie_talkie" && (
              <div className="border-t border-[#0e1621] transition-all bg-gradient-to-r from-red-900/50 to-[#17212b] border-red-600/30">
                <div className="p-3">
                  <div className="max-w-3xl mx-auto flex flex-col items-center justify-center">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                        <div className="absolute inset-0 w-4 h-4 bg-red-500 rounded-full animate-ping opacity-50"></div>
                      </div>
                      <span className="text-white font-bold text-lg">{formatTime(recordingTime)}</span>
                      <span className="text-red-400 font-medium">Grabando...</span>
                    </div>
                    <div className="flex space-x-3 mt-3">
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={cancelRecording}>
                        <X className="w-4 h-4 mr-1" />
                        Cancelar
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-red-600 hover:bg-red-700 text-white" 
                        onClick={stopRecording}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Enviar
                      </Button>
                    </div>
                    {isTranscribing && (
                      <div className="mt-4 flex items-center text-sm text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Transcribiendo...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ========== NUEVO DISEÑO: Input Area con Pestañas Estilizadas ==========
                IMPORTANTE: en el tab "walkie_talkie" siempre se muestra el panel
                (incluso durante grabación) porque su botón toggle es quien maneja
                el estado de grabar/detener. */}
            {(!isRecording || activeInputTab === "walkie_talkie") && (
              <div className="bg-[#1a1d24] border-t border-[#2a2d35]">
                
                {/* Pestañas Estilizadas - Rectángulos redondeados separados */}
                <div className="flex justify-center gap-2 px-4 py-3">
                  <button
                    onClick={() => setActiveInputTab("chat")}
                    className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                      activeInputTab === "chat"
                        ? "bg-[#2d3139] text-white shadow-lg"
                        : "bg-transparent text-slate-500 hover:text-slate-300 hover:bg-[#252830]"
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    OkChat
                  </button>
                  <button
                    onClick={() => setActiveInputTab("walkie_talkie")}
                    className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                      activeInputTab === "walkie_talkie"
                        ? "bg-[#2d3139] text-white shadow-lg"
                        : "bg-transparent text-slate-500 hover:text-slate-300 hover:bg-[#252830]"
                    }`}
                  >
                    <Radio className="w-4 h-4" />
                    Walkie Talkie
                  </button>
                </div>

                {/* ===== TAB CHAT: Input amplio con controles DENTRO ===== */}
                {activeInputTab === "chat" && (
                  <div className="px-4 pb-4">
                    {/* Reply Preview */}
                    {replyingTo && (
                      <div className="max-w-3xl mx-auto mb-2">
                        <div className="flex items-center justify-between bg-[#252830] rounded-lg px-3 py-2 border-l-4 border-red-500">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-red-400 font-medium flex items-center">
                              <Reply className="w-3 h-3 mr-1" />
                              Respondiendo a {replyingTo.sender_info?.name || "Usuario"}
                            </p>
                            <p className="text-sm text-slate-300 truncate">{replyingTo.content?.substring(0, 100)}</p>
                          </div>
                          <button onClick={() => setReplyingTo(null)} className="p-1 hover:bg-slate-700 rounded ml-2">
                            <X className="w-4 h-4 text-slate-400" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Input Container Amplio - Controles DENTRO */}
                    <div className="max-w-3xl mx-auto">
                      <div className="bg-[#252830] rounded-2xl border border-[#3a3d45] overflow-hidden">
                        {/* Textarea */}
                        <div className="p-3">
                          <textarea
                            placeholder="Escribe un mensaje..."
                            value={messageInput}
                            onChange={(e) => { setMessageInput(e.target.value); handleTyping(); }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                            }}
                            rows={1}
                            className="w-full bg-transparent text-white placeholder:text-slate-500 focus:outline-none resize-none min-h-[24px] max-h-[100px] overflow-y-auto text-[15px]"
                            disabled={uploading}
                          />
                        </div>
                        
                        {/* Barra de controles DENTRO del input */}
                        <div className="flex items-center justify-between px-3 py-2 border-t border-[#3a3d45] bg-[#22252d]">
                          {/* Controles izquierda */}
                          <div className="flex items-center gap-1">
                            {/* Adjuntos */}
                            <DropdownMenu open={showAttachMenu} onOpenChange={setShowAttachMenu}>
                              <DropdownMenuTrigger asChild>
                                <button className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d45] transition-colors">
                                  <Paperclip className="w-5 h-5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="bg-[#252830] border-[#3a3d45] w-56">
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}>
                                  <ImageIcon className="w-4 h-4 mr-3 text-red-400" /> Foto
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { videoInputRef.current?.click(); setShowAttachMenu(false); }}>
                                  <Video className="w-4 h-4 mr-3 text-red-400" /> Video
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false); }}>
                                  <FileText className="w-4 h-4 mr-3 text-blue-400" /> Documento
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-[#3a3d45]" />
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { viewOnceImageRef.current?.click(); setShowAttachMenu(false); }}>
                                  <Eye className="w-4 h-4 mr-3 text-purple-400" /> Foto (Ver una vez)
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { viewOnceVideoRef.current?.click(); setShowAttachMenu(false); }}>
                                  <Eye className="w-4 h-4 mr-3 text-purple-400" /> Video (Ver una vez)
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-[#3a3d45]" />
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { setShowTemporaryMessageDialog(true); setShowAttachMenu(false); }}>
                                  <Clock className="w-4 h-4 mr-3 text-orange-400" /> Mensaje temporal
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-[#3a3d45]" />
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { setShowLocationDialog(true); getCurrentLocation(); setShowAttachMenu(false); }}>
                                  <MapPin className="w-4 h-4 mr-3 text-green-400" /> Ubicación
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-[#3a3d45]" />
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { setShowProductPicker(true); setShowAttachMenu(false); }}>
                                  <ShoppingBag className="w-4 h-4 mr-3 text-emerald-400" /> Producto del catálogo
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-[#3a3d45]" />
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { setShowScheduledDialog(true); setShowAttachMenu(false); }}>
                                  <Calendar className="w-4 h-4 mr-3 text-blue-400" /> Mensaje programado
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { setShowStickerDialog(true); setShowAttachMenu(false); }}>
                                  <StickerIcon className="w-4 h-4 mr-3 text-pink-400" /> Sticker
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-white hover:bg-[#3a3d45] cursor-pointer" onClick={() => { setShowMiniAppDialog(true); setShowAttachMenu(false); }}>
                                  <Puzzle className="w-4 h-4 mr-3 text-purple-400" /> Mini-app
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar" onChange={(e) => handleFileUpload(e, "document")} />
                            <input type="file" ref={imageInputRef} className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, "image")} />
                            <input type="file" ref={videoInputRef} className="hidden" accept="video/*" onChange={(e) => handleFileUpload(e, "video")} />
                            <input type="file" ref={viewOnceImageRef} className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, "image", true)} />
                            <input type="file" ref={viewOnceVideoRef} className="hidden" accept="video/*" onChange={(e) => handleFileUpload(e, "video", true)} />

                            {/* GIF */}
                            <DropdownMenu open={showGifPicker} onOpenChange={setShowGifPicker}>
                              <DropdownMenuTrigger asChild>
                                <button className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d45] transition-colors">
                                  <span className="text-xs font-bold">GIF</span>
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="bg-[#252830] border-[#3a3d45] w-80 p-2">
                                <Input
                                  placeholder="Buscar GIFs..."
                                  value={gifSearchQuery}
                                  onChange={(e) => { setGifSearchQuery(e.target.value); searchGifs(e.target.value); }}
                                  className="mb-2 bg-[#1a1d24] border-[#3a3d45] text-white placeholder:text-slate-500"
                                />
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {loadingGifs ? (
                                    <div className="col-span-2 flex justify-center py-4">
                                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                                    </div>
                                  ) : gifs.length > 0 ? (
                                    gifs.map(gif => (
                                      <img key={gif.id} src={gif.preview || gif.url} alt={gif.title} className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80" onClick={() => sendGif(gif.url)} />
                                    ))
                                  ) : (
                                    <p className="col-span-2 text-center text-slate-500 py-4 text-sm">Busca GIFs...</p>
                                  )}
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Buscar */}
                            <button 
                              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d45] transition-colors"
                              onClick={() => setShowChatSearch(true)}
                            >
                              <Search className="w-5 h-5" />
                            </button>
                          </div>

                          {/* Controles derecha */}
                          <div className="flex items-center gap-1">
                            {/* Emojis */}
                            <DropdownMenu open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                              <DropdownMenuTrigger asChild>
                                <button className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d45] transition-colors">
                                  <Smile className="w-5 h-5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#252830] border-[#3a3d45] w-72 p-2">
                                <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                                  {EMOJI_LIST.map(emoji => (
                                    <button key={emoji} onClick={() => insertEmoji(emoji)} className="p-1.5 hover:bg-[#3a3d45] rounded text-xl">
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Micrófono */}
                            <button 
                              onClick={() => startRecording("normal")}
                              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#3a3d45] transition-colors"
                            >
                              <Mic className="w-5 h-5" />
                            </button>

                            {/* Botón Enviar */}
                            <button 
                              onClick={sendMessage} 
                              disabled={uploading || !messageInput.trim()} 
                              className={`p-2.5 rounded-xl transition-all ${
                                messageInput.trim() 
                                  ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30' 
                                  : 'bg-[#3a3d45] text-slate-500'
                              }`}
                            >
                              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ===== TAB WALKIE TALKIE: Área expandida hacia arriba ===== */}
                {activeInputTab === "walkie_talkie" && (
                  <div className="px-4 pb-4">
                    <div className="max-w-3xl mx-auto bg-[#252830] rounded-2xl border border-[#3a3d45] overflow-hidden" style={{ minHeight: "220px" }}>
                      {/* Header con contador */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-[#3a3d45]">
                        <div className="flex items-center gap-3">
                          <span className="text-white font-medium">{messages.length} msjs</span>
                        </div>
                        <button 
                          onClick={() => setShowHistoryModal(true)}
                          className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
                        >
                          <History className="w-4 h-4" />
                          Chat (Historial)
                        </button>
                      </div>

                      {/* SOLUCIÓN DEFINITIVA: TOGGLE puro por click.
                          Tras múltiples intentos con touch handlers y pointer events que
                          fallaban en distintos dispositivos, usamos el camino más robusto:
                          un único `onClick`. Funciona idénticamente en móvil y escritorio
                          porque el navegador SIEMPRE genera un click sintético tras un tap,
                          y nunca falla. Sin pointer capture, sin diferenciación de dispositivos.
                          
                          Comportamiento:
                            - Botón ROJO + leyenda "Presiona el botón para hablar".
                            - Click → empieza a grabar, botón cambia a VERDE,
                              leyenda: "Grabando, cuando termines presiona el botón nuevamente".
                            - Click de nuevo → detiene, envía y vuelve a ROJO. */}
                      <div className="flex flex-col items-center justify-center py-8">
                        {(() => {
                          const recording = isRecording && recordingType === "walkie_talkie";
                          return (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  if (recording) {
                                    stopRecording();
                                  } else if (!isRecording) {
                                    startRecording("walkie_talkie");
                                  }
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                                className={`w-28 h-28 rounded-full flex items-center justify-center transition-all transform hover:scale-105 active:scale-95 shadow-2xl select-none ${
                                  recording
                                    ? "bg-gradient-to-br from-green-500 to-green-700 ring-4 ring-green-300 animate-pulse shadow-green-600/60"
                                    : "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 shadow-red-500/40"
                                }`}
                                style={{ WebkitTapHighlightColor: 'transparent', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                                aria-label={recording ? "Detener y enviar" : "Empezar a hablar"}
                              >
                                <Mic className="w-12 h-12 text-white pointer-events-none" />
                              </button>
                              <p className={`text-sm mt-4 text-center px-4 max-w-xs font-medium ${recording ? "text-green-400" : "text-slate-400"}`}>
                                {recording
                                  ? "Grabando, cuando termines presiona el botón nuevamente"
                                  : "Presiona el botón para hablar"}
                              </p>
                              {recording && (
                                <p className="text-xs text-slate-500 mt-2">
                                  {formatTime(recordingTime)}
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {/* Barra de controles inferior */}
                      <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-[#3a3d45] bg-[#22252d]">
                        {/* Filtro de Voz */}
                        <button
                          onClick={() => setShowVoiceFilterModal(true)}
                          className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-colors ${
                            selectedVoiceFilter !== "normal" 
                              ? "bg-purple-600/20 text-purple-300 border border-purple-500" 
                              : "bg-[#3a3d45] text-slate-300 hover:bg-[#4a4d55]"
                          }`}
                        >
                          <Settings className="w-4 h-4" />
                          Filtro de Voz
                        </button>

                        {/* Traductor */}
                        <button
                          onClick={() => setShowTranslatorModal(true)}
                          className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-colors ${
                            voiceSettings.autoTranslate 
                              ? "bg-blue-600/20 text-blue-300 border border-blue-500" 
                              : "bg-[#3a3d45] text-slate-300 hover:bg-[#4a4d55]"
                          }`}
                        >
                          <Languages className="w-4 h-4" />
                          Traductor
                        </button>

                        {/* GIF */}
                        <DropdownMenu onOpenChange={(open) => { if (open && gifs.length === 0) fetchTrendingGifs(true); }}>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 rounded-full bg-[#3a3d45] text-slate-300 hover:bg-[#4a4d55] transition-colors">
                              <span className="text-xs font-bold">GIF</span>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center" className="bg-[#252830] border-[#3a3d45] w-96 p-2">
                            <Input
                              placeholder="Buscar GIFs..."
                              value={gifSearchQuery}
                              onChange={(e) => { setGifSearchQuery(e.target.value); searchGifs(e.target.value, true); }}
                              className="mb-2 bg-[#1a1d24] border-[#3a3d45] text-white placeholder:text-slate-500"
                            />
                            <div
                              ref={gifScrollRef}
                              onScroll={handleGifScroll}
                              className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto"
                            >
                              {loadingGifs && gifs.length === 0 ? (
                                <div className="col-span-2 flex items-center justify-center py-8">
                                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                                </div>
                              ) : gifs.length === 0 ? (
                                <div className="col-span-2 text-center py-8 text-slate-500 text-sm">
                                  No hay GIFs
                                </div>
                              ) : (
                                <>
                                  {gifs.map((gif, idx) => (
                                    <img
                                      key={`${gif.id}-${idx}`}
                                      src={gif.preview || gif.url}
                                      alt={gif.title}
                                      loading="lazy"
                                      className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() => sendGif(gif.url)}
                                    />
                                  ))}
                                  {loadingMoreGifs && (
                                    <div className="col-span-2 flex items-center justify-center py-3">
                                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                    </div>
                                  )}
                                  {!gifHasMore && gifs.length > 0 && (
                                    <div className="col-span-2 text-center py-2 text-xs text-slate-500">
                                      Fin de los GIFs
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Buscar */}
                        <button 
                          onClick={() => setShowChatSearch(true)}
                          className="p-2 rounded-full bg-[#3a3d45] text-slate-300 hover:bg-[#4a4d55] transition-colors"
                        >
                          <Search className="w-5 h-5" />
                        </button>

                        {/* Transcripción Toggle */}
                        <button
                          onClick={() => setVoiceSettings({...voiceSettings, autoTranscribe: !voiceSettings.autoTranscribe})}
                          className={`p-2 rounded-full transition-colors ${
                            voiceSettings.autoTranscribe 
                              ? "bg-green-600 text-white" 
                              : "bg-[#3a3d45] text-slate-300 hover:bg-[#4a4d55]"
                          }`}
                          title={voiceSettings.autoTranscribe ? "Transcripción ON" : "Transcripción OFF"}
                        >
                          <FileText className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* Vista principal del chat - Sin pantalla de bienvenida */
          <div className="flex-1 flex flex-col bg-[#0e1621]">
            {/* Header móvil */}
            <div className="h-14 bg-[#17212b] border-b border-[#0e1621] flex items-center justify-between px-4 md:hidden">
              <button 
                className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-colors"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="w-5 h-5 text-white" />
              </button>
              <img src="/logo-okgo-load.png" alt="OkChat" className="h-8" />
              <button 
                className="w-10 h-10 rounded-full bg-[#232e3c] hover:bg-[#2b3a4c] flex items-center justify-center transition-colors"
                onClick={() => setShowNewChatDialog(true)}
              >
                <UserPlus className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Barra de búsqueda de usuarios */}
            <div className="p-4 bg-[#17212b]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="text"
                  placeholder="Buscar usuario para chatear..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#242f3d] border-transparent text-white placeholder:text-slate-500 h-11"
                />
              </div>
            </div>

            {/* Lista de conversaciones o resultados de búsqueda */}
            <ScrollArea className="flex-1">
              <div className="p-4">
                {searchQuery.trim().length >= 2 ? (
                  /* Resultados de búsqueda de usuarios */
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center">
                      <Search className="w-3 h-3 mr-2" />
                      Buscar usuarios
                    </p>
                    {users.filter(u => 
                      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      u.nickname?.toLowerCase().includes(searchQuery.toLowerCase())
                    ).length > 0 ? (
                      users.filter(u => 
                        u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        u.nickname?.toLowerCase().includes(searchQuery.toLowerCase())
                      ).map(u => (
                        <div
                          key={u.user_id}
                          className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#232e3c] transition-colors bg-[#1e2a36]"
                          onClick={async () => {
                            // Iniciar chat con este usuario
                            try {
                              const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
                              const response = await fetch(`${API}/conversations`, {
                                method: "POST",
                                headers,
                                credentials: "include",
                                body: JSON.stringify({ participant_ids: [u.user_id], is_group: false }),
                              });
                              if (response.ok) {
                                const conv = await response.json();
                                setSelectedConversation(conv);
                                fetchMessages(conv.conversation_id);
                                fetchConversations();
                                setSearchQuery("");
                                toast.success("Chat iniciado");
                              }
                            } catch (err) {
                              toast.error("Error al iniciar chat");
                            }
                          }}
                        >
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">
                              {u.name?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{u.name}</p>
                            <p className="text-xs text-slate-400 truncate">@{u.nickname}</p>
                          </div>
                          {u.status === 'online' && (
                            <span className="w-2.5 h-2.5 bg-green-500 rounded-full" />
                          )}
                          <MessageSquare className="w-5 h-5 text-red-400" />
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No se encontraron usuarios</p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Lista de conversaciones */
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center">
                      <MessageSquare className="w-3 h-3 mr-2" />
                      Conversaciones ({conversations.length})
                    </p>
                    {conversations.length > 0 ? (
                      conversations.map(conv => {
                        const otherUser = conv.participant_info?.find(p => p.user_id !== user?.user_id);
                        const convName = conv.is_group ? conv.name : (otherUser?.name || "Chat");
                        const isOnline = otherUser?.status === 'online';
                        
                        return (
                          <div
                            key={conv.conversation_id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              selectedConversation?.conversation_id === conv.conversation_id
                                ? 'bg-[#2b5278]'
                                : 'hover:bg-[#232e3c] bg-[#1e2a36]'
                            }`}
                            onClick={() => {
                              setSelectedConversation(conv);
                              fetchMessages(conv.conversation_id);
                            }}
                          >
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={otherUser?.picture} />
                              <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">
                                {convName.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-white font-medium truncate">{convName}</p>
                                {conv.last_message_time && (
                                  <span className="text-xs text-slate-500">
                                    {new Date(conv.last_message_time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-400 truncate">{conv.last_message?.content || "Sin mensajes"}</p>
                            </div>
                            {isOnline && (
                              <span className="w-2.5 h-2.5 bg-green-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p className="text-lg mb-2">No tienes conversaciones</p>
                        <p className="text-sm mb-4">Busca un usuario arriba para iniciar un chat</p>
                      </div>
                    )}
                    
                    {/* Load more conversations */}
                    {hasMoreConversations && conversations.length > 0 && (
                      <div className="flex justify-center py-3">
                        <button
                          onClick={loadMoreConversations}
                          disabled={loadingMoreConversations}
                          className="text-sm text-slate-400 hover:text-white px-4 py-2 rounded-lg bg-[#1e2a36] hover:bg-[#2b3a4c] transition-colors disabled:opacity-50"
                        >
                          {loadingMoreConversations ? (
                            <span className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Cargando...
                            </span>
                          ) : (
                            'Cargar más'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Delete Message Confirmation */}
      <AlertDialog open={!!messageToDelete} onOpenChange={() => setMessageToDelete(null)}>
        <AlertDialogContent className="bg-[#17212b] border-[#0e1621]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar mensaje?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#232e3c] text-white border-[#0e1621] hover:bg-[#2b3a4c]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMessage(messageToDelete)} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CreditCard className="w-5 h-5 mr-2 text-green-400" /> Enviar pago
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-2">Destinatario</p>
              <p className="text-white font-medium">{getConversationName(selectedConversation)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-2">Moneda</p>
              <Select value={paymentCurrency} onValueChange={setPaymentCurrency}>
                <SelectTrigger className="bg-[#242f3d] border-transparent text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#232e3c] border-[#0e1621]">
                  {currencies.map(c => (
                    <SelectItem key={c.code} value={c.code} className="text-white hover:bg-[#2b5278]">
                      {c.symbol} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-2">Monto</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{getCurrencySymbol(paymentCurrency)}</span>
                <Input type="number" placeholder="0.00" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} min="1" max="10000" step="0.01" className="pl-8 bg-[#242f3d] border-transparent text-white placeholder:text-slate-500 text-lg" />
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-2">Mensaje (opcional)</p>
              <Input placeholder="¿Para qué es este pago?" value={paymentMessage} onChange={(e) => setPaymentMessage(e.target.value)} className="bg-[#242f3d] border-transparent text-white placeholder:text-slate-500" />
            </div>
            <Button onClick={createPayment} className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) < 1}>
              {processingPayment ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</> : <><CreditCard className="w-4 h-4 mr-2" /> Pagar {getCurrencySymbol(paymentCurrency)}{paymentAmount || '0.00'} {paymentCurrency}</>}
            </Button>
            <p className="text-xs text-slate-500 text-center">Serás redirigido a Stripe para completar el pago.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={showPaymentHistory} onOpenChange={setShowPaymentHistory}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <History className="w-5 h-5 mr-2 text-red-400" /> Historial de Pagos
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-96">
            {loadingHistory ? (
              <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>
            ) : paymentHistory.length > 0 ? (
              <div className="space-y-3">
                {paymentHistory.map(txn => (
                  <div key={txn.transaction_id} className={`p-3 rounded-lg ${txn.sender_id === user?.user_id ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">
                          {txn.sender_id === user?.user_id ? `Enviado a ${txn.recipient_name}` : `Recibido de ${txn.sender_name}`}
                        </p>
                        <p className="text-xs text-slate-500">{new Date(txn.created_at).toLocaleString('es-MX')}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${txn.sender_id === user?.user_id ? 'text-red-400' : 'text-green-400'}`}>
                          {txn.sender_id === user?.user_id ? '-' : '+'}{getCurrencySymbol(txn.currency?.toUpperCase())}{txn.amount?.toFixed(2)}
                        </p>
                        <p className={`text-xs ${txn.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {txn.payment_status === 'paid' ? 'Completado' : 'Pendiente'}
                        </p>
                      </div>
                    </div>
                    {txn.message && <p className="text-sm text-slate-400 mt-2">"{txn.message}"</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No hay transacciones</p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <MapPin className="w-5 h-5 mr-2 text-green-400" /> Compartir Ubicación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingLocation ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-red-600 mb-4" />
                <p className="text-slate-400">Obteniendo ubicación...</p>
              </div>
            ) : currentLocation ? (
              <>
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${currentLocation.lat},${currentLocation.lng}&zoom=15&size=400x200&markers=color:red%7C${currentLocation.lat},${currentLocation.lng}&key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8`}
                    alt="Mapa"
                    className="w-full"
                  />
                </div>
                <p className="text-sm text-slate-400 text-center">
                  Lat: {currentLocation.lat.toFixed(6)}, Lng: {currentLocation.lng.toFixed(6)}
                </p>
                <Button onClick={sendLocation} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <Send className="w-4 h-4 mr-2" /> Enviar Ubicación
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No se pudo obtener la ubicación</p>
                <Button onClick={getCurrentLocation} variant="outline" className="border-slate-600 text-slate-300">
                  Reintentar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* File Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white">{previewFile?.name || "Vista previa"}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center">
            {previewFile?.type === "image" && <img src={previewFile.url} alt="" className="max-w-full max-h-[70vh] rounded-lg" />}
            {previewFile?.type === "video" && <video src={previewFile.url} controls className="max-w-full max-h-[70vh] rounded-lg" />}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Chat Dialog - Using new component */}
      <NewChatDialog
        open={showNewChatDialog}
        onOpenChange={setShowNewChatDialog}
        onSelectConversation={(conv) => {
          // NUEVO: Seleccionar conversación directamente sin navegación
          setSelectedConversation(conv);
          fetchMessages(conv.conversation_id);
          fetchConversations();
        }}
        onOpenProfile={(user) => {
          setSelectedUserProfile(user);
          setShowUserProfile(true);
          setShowNewChatDialog(false);
        }}
        currentUserId={user?.user_id}
        existingConversations={conversations}
      />

      {/* Temporary Message Dialog */}
      <Dialog open={showTemporaryDialog} onOpenChange={setShowTemporaryDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Clock className="w-5 h-5 mr-2 text-orange-400" />
              Mensaje temporal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Los mensajes temporales se eliminan automáticamente después del tiempo seleccionado.
            </p>
            <div className="flex gap-2">
              {[15, 45, 60].map(mins => (
                <button
                  key={mins}
                  onClick={() => setTemporaryMinutes(mins)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    temporaryMinutes === mins
                      ? "bg-orange-500 text-white"
                      : "bg-[#242f3d] text-slate-400 hover:bg-[#2b3a4c]"
                  }`}
                >
                  {mins === 60 ? "1 hora" : `${mins} min`}
                </button>
              ))}
            </div>
            <Input
              placeholder="Escribe tu mensaje temporal..."
              value={temporaryContent}
              onChange={(e) => setTemporaryContent(e.target.value)}
              className="bg-[#242f3d] border-transparent text-white placeholder:text-slate-500"
            />
            <Button 
              onClick={sendTemporaryMessage} 
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              disabled={!temporaryContent.trim()}
            >
              <Clock className="w-4 h-4 mr-2" />
              Enviar mensaje temporal ({temporaryMinutes === 60 ? "1 hora" : `${temporaryMinutes} min`})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Profile Dialog */}
      <Dialog open={showUserProfile} onOpenChange={setShowUserProfile}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Perfil de usuario
            </DialogTitle>
          </DialogHeader>
          {selectedUserProfile && (
            <div className="space-y-4">
              {/* Profile Picture */}
              <div className="flex flex-col items-center">
                <Avatar className="w-24 h-24 border-4 border-red-500/30">
                  <AvatarImage src={selectedUserProfile.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white text-3xl">
                    {selectedUserProfile.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-semibold text-white mt-3">
                  {selectedUserProfile.name}
                </h3>
                {selectedUserProfile.nickname && (
                  <p className="text-slate-400 text-sm">@{selectedUserProfile.nickname}</p>
                )}
                {selectedUserProfile.status === 'online' && (
                  <span className="flex items-center text-green-400 text-xs mt-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                    En línea
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                {/* BOTÓN CHATEAR - Sin navegación, selección directa */}
                <button 
                  type="button"
                  onClick={async () => {
                    try {
                      const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
                      
                      const response = await fetch(`${API}/conversations`, {
                        method: "POST",
                        headers: headers,
                        credentials: "include",
                        body: JSON.stringify({ participant_ids: [selectedUserProfile.user_id], is_group: false }),
                      });
                      
                      if (response.ok) {
                        const conv = await response.json();
                        // NUEVO: Seleccionar conversación sin navegación
                        setShowUserProfile(false);
                        setSelectedConversation(conv);
                        fetchMessages(conv.conversation_id);
                        fetchConversations();
                        toast.success("Chat iniciado");
                      } else {
                        let errorMsg = "Error al iniciar chat";
                        try {
                          const error = await response.json();
                          errorMsg = error.detail || errorMsg;
                        } catch (parseErr) {}
                        toast.error(errorMsg);
                      }
                    } catch (err) {
                      toast.error("Error de conexión");
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-md font-medium select-none"
                  data-testid="profile-chat-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  Chatear
                </button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full border-slate-600 text-white hover:bg-[#232e3c]"
                      disabled={savingToCategory}
                    >
                      {savingToCategory ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Users className="w-4 h-4 mr-2" />
                      )}
                      Guardar en contactos
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#232e3c] border-[#0e1621] z-[200]">
                    <DropdownMenuItem 
                      onClick={() => saveUserToCategory(selectedUserProfile.user_id, "contacts")}
                      className="text-white hover:bg-[#2b5278] cursor-pointer"
                    >
                      <Users className="w-4 h-4 mr-2" /> Contactos
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => saveUserToCategory(selectedUserProfile.user_id, "stores")}
                      className="text-white hover:bg-[#2b5278] cursor-pointer"
                    >
                      <Store className="w-4 h-4 mr-2" /> Mis Tiendas
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => saveUserToCategory(selectedUserProfile.user_id, "restaurants")}
                      className="text-white hover:bg-[#2b5278] cursor-pointer"
                    >
                      <UtensilsCrossed className="w-4 h-4 mr-2" /> Mis Restaurantes
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button 
                  onClick={() => shareUserProfile(selectedUserProfile)}
                  variant="outline" 
                  className="w-full border-slate-600 text-white hover:bg-[#232e3c]"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartir perfil
                </Button>

                {selectedUserProfile.nickname && (
                  <Button 
                    onClick={() => window.open(`/u/${selectedUserProfile.nickname}`, '_blank')}
                    variant="ghost" 
                    className="w-full text-slate-400 hover:text-white hover:bg-[#232e3c]"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Ver perfil público
                  </Button>
                )}

                {/* QR Code and Link Section */}
                {selectedUserProfile?.nickname && (
                  <div className="pt-4 border-t border-slate-700 space-y-3">
                    <p className="text-xs text-slate-500 text-center uppercase tracking-wider">Compartir</p>
                    
                    {/* QR Code - Bigger */}
                    {profileQrCode && (
                      <div className="flex justify-center">
                        <div className="bg-white p-3 rounded-lg shadow-lg">
                          <img src={profileQrCode} alt="QR" className="w-36 h-36" />
                        </div>
                      </div>
                    )}
                    
                    {/* Profile Link */}
                    <div className="flex items-center gap-2 justify-center bg-slate-800 px-3 py-2 rounded-lg max-w-[220px] mx-auto">
                      <code className="text-xs text-slate-400 truncate flex-1">
                        {`${window.location.origin}/u/${selectedUserProfile.nickname}`}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/u/${selectedUserProfile.nickname}`);
                          toast.success("Link copiado");
                        }}
                        className="text-slate-500 hover:text-white p-1 shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Group Dialog */}
      <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
        <DialogContent className="bg-[#17212b] border-[#0e1621] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Nuevo grupo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Nombre del grupo" value={groupName} onChange={(e) => setGroupName(e.target.value)} className="bg-[#242f3d] border-transparent text-white placeholder:text-slate-500" />
            <Input placeholder="Buscar participantes..." value={userSearch} onChange={(e) => { setUserSearch(e.target.value); fetchUsers(e.target.value); }} className="bg-[#242f3d] border-transparent text-white placeholder:text-slate-500" />
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(userId => {
                  const u = users.find(usr => usr.user_id === userId);
                  return u ? (
                    <span key={userId} className="bg-red-600/30 text-red-300 px-3 py-1 rounded-full text-sm flex items-center">
                      {u.name}
                      <button onClick={() => setSelectedUsers(prev => prev.filter(id => id !== userId))} className="ml-2"><X className="w-3 h-3" /></button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
            <ScrollArea className="h-48">
              <div className="space-y-1">
                {filteredUsers.map((u) => (
                  <div
                    key={u.user_id}
                    onClick={() => setSelectedUsers(prev => prev.includes(u.user_id) ? prev.filter(id => id !== u.user_id) : [...prev, u.user_id])}
                    className={`flex items-center p-3 rounded-lg cursor-pointer ${selectedUsers.includes(u.user_id) ? 'bg-red-600/20' : 'hover:bg-[#232e3c]'}`}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback className="bg-gradient-to-br from-red-500 to-purple-600 text-white">{u.name.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="ml-3"><p className="font-medium text-white">{u.name}</p></div>
                    {selectedUsers.includes(u.user_id) && <Check className="ml-auto w-5 h-5 text-red-400" />}
                  </div>
                ))}
              </div>
            </ScrollArea>
            <Button onClick={createGroup} className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={!groupName.trim() || selectedUsers.length === 0}>
              Crear grupo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block User Confirmation Dialog */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent className="bg-[#1a1a24] border-slate-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Bloquear usuario?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              ¿Estás seguro que deseas bloquear a este usuario? No podrá enviarte mensajes y no verás sus mensajes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleBlockUser} className="bg-red-600 hover:bg-red-700">
              Bloquear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Global Search Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Búsqueda Global
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Buscar usuarios o mensajes..."
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700"
                  autoFocus
                />
              </div>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded-md px-3 text-sm"
              >
                <option value="all">Todo</option>
                <option value="users">Usuarios</option>
                <option value="messages">Mensajes</option>
              </select>
            </div>

            <ScrollArea className="h-[400px]">
              {searchingGlobal ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Users Results */}
                  {searchResults.users?.length > 0 && (
                    <div>
                      <h4 className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Usuarios ({searchResults.users.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.users.map((u) => (
                          <div
                            key={u.user_id}
                            onClick={() => {
                              navigate(`/u/${u.nickname}`);
                              setShowSearchDialog(false);
                            }}
                            className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors"
                          >
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={u.picture ? `${BACKEND_URL}${u.picture}` : null} />
                              <AvatarFallback className="bg-red-600 text-white">
                                {u.name?.charAt(0)?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-slate-400">@{u.nickname}</p>
                            </div>
                            {u.status === 'online' && (
                              <span className="text-xs text-green-400">● En línea</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Messages Results */}
                  {searchResults.messages?.length > 0 && (
                    <div>
                      <h4 className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Mensajes ({searchResults.total_messages || searchResults.messages.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.messages.map((msg) => (
                          <div
                            key={msg.message_id}
                            onClick={() => {
                              navigate(`/chat/${msg.conversation_id}`);
                              setShowSearchDialog(false);
                            }}
                            className="p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-red-400">{msg.sender_info?.name}</span>
                              <span className="text-xs text-slate-500">
                                {new Date(msg.created_at).toLocaleString('es-MX')}
                              </span>
                            </div>
                            <p className="text-slate-200 line-clamp-2">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results */}
                  {globalSearchQuery.length >= 2 && 
                   !searchingGlobal && 
                   searchResults.users?.length === 0 && 
                   searchResults.messages?.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron resultados para "{globalSearchQuery}"</p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Search Dialog (within conversation) */}
      <Dialog open={showChatSearch} onOpenChange={(open) => {
        setShowChatSearch(open);
        if (!open) {
          setChatSearchQuery("");
          setChatSearchResults([]);
        }
      }}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white max-w-lg max-h-[70vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Buscar en esta conversación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Buscar mensajes..."
                value={chatSearchQuery}
                onChange={(e) => setChatSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
                autoFocus
              />
            </div>

            <ScrollArea className="h-[300px]">
              {searchingChat ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                </div>
              ) : chatSearchResults.length > 0 ? (
                <div className="space-y-2">
                  {chatSearchResults.map((msg) => (
                    <div
                      key={msg.message_id}
                      className="p-3 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors"
                      onClick={() => {
                        scrollToMessage(msg.message_id);
                        setShowChatSearch(false);
                        setChatSearchQuery("");
                        setChatSearchResults([]);
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-red-400">{msg.sender_info?.name}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-slate-500">
                            {new Date(msg.created_at).toLocaleString('es-MX')}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-red-400 hover:text-red-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToMessage(msg.message_id);
                              setShowChatSearch(false);
                              setChatSearchQuery("");
                              setChatSearchResults([]);
                            }}
                          >
                            Ir al mensaje →
                          </Button>
                        </div>
                      </div>
                      <p className="text-slate-200">{msg.content}</p>
                    </div>
                  ))}
                </div>
              ) : chatSearchQuery.length >= 2 ? (
                <div className="text-center py-8 text-slate-400">
                  <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No se encontraron mensajes</p>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <p>Escribe al menos 2 caracteres para buscar</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Once Media Viewer */}
      <Dialog open={showViewOnceViewer} onOpenChange={(open) => {
        if (!open && viewOnceMedia) {
          // Mark as viewed when closing
          setViewedOnceMessages(prev => new Set([...prev, viewOnceMedia.messageId]));
          // Delete the message after viewing
          deleteViewOnceMessage(viewOnceMedia.messageId);
          setViewOnceMedia(null);
        }
        setShowViewOnceViewer(open);
      }}>
        <DialogContent className="bg-black/95 border-none max-w-4xl p-0">
          <div className="relative min-h-[50vh] flex items-center justify-center p-4">
            <div className="absolute top-4 left-4 flex items-center space-x-2 text-white bg-black/50 px-3 py-2 rounded-full">
              <Eye className="w-4 h-4 text-purple-400" />
              <span className="text-sm">Se eliminará al cerrar</span>
            </div>
            <button 
              onClick={() => setShowViewOnceViewer(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            {viewOnceMedia?.type === "image" && (
              <img src={viewOnceMedia.url} alt="Ver una vez" className="max-w-full max-h-[80vh] rounded-lg" />
            )}
            {viewOnceMedia?.type === "video" && (
              <video src={viewOnceMedia.url} controls autoPlay className="max-w-full max-h-[80vh] rounded-lg" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Settings Dialog */}
      <Dialog open={showVoiceSettings} onOpenChange={setShowVoiceSettings}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-red-500" />
              Configuración de Voz
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Auto Transcribe */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Auto-transcribir</p>
                <p className="text-sm text-slate-400">Convertir audio a texto automáticamente</p>
              </div>
              <Button
                variant={voiceSettings.autoTranscribe ? "default" : "outline"}
                size="sm"
                className={voiceSettings.autoTranscribe ? "bg-red-600" : "border-slate-600"}
                onClick={() => setVoiceSettings({...voiceSettings, autoTranscribe: !voiceSettings.autoTranscribe})}
              >
                {voiceSettings.autoTranscribe ? "ON" : "OFF"}
              </Button>
            </div>
            
            {/* Auto Play */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Auto-reproducir</p>
                <p className="text-sm text-slate-400">Reproducir mensajes de voz al recibir</p>
              </div>
              <Button
                variant={voiceSettings.autoPlay ? "default" : "outline"}
                size="sm"
                className={voiceSettings.autoPlay ? "bg-red-600" : "border-slate-600"}
                onClick={() => setVoiceSettings({...voiceSettings, autoPlay: !voiceSettings.autoPlay})}
              >
                {voiceSettings.autoPlay ? "ON" : "OFF"}
              </Button>
            </div>
            
            {/* Show Transcription */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Mostrar transcripción</p>
                <p className="text-sm text-slate-400">Ver texto junto al audio</p>
              </div>
              <Button
                variant={voiceSettings.showTranscription ? "default" : "outline"}
                size="sm"
                className={voiceSettings.showTranscription ? "bg-red-600" : "border-slate-600"}
                onClick={() => setVoiceSettings({...voiceSettings, showTranscription: !voiceSettings.showTranscription})}
              >
                {voiceSettings.showTranscription ? "ON" : "OFF"}
              </Button>
            </div>

            {/* Auto-translate chats (NEW) */}
            <div className="space-y-2 border-t border-slate-700 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Languages className="w-4 h-4 text-blue-400" /> Traducir chats automáticamente
                  </p>
                  <p className="text-sm text-slate-400">Mostrar traducción debajo de cada mensaje recibido</p>
                </div>
                <Button
                  variant={autoTranslateSettings.auto_translate_enabled ? "default" : "outline"}
                  size="sm"
                  className={autoTranslateSettings.auto_translate_enabled ? "bg-blue-600 hover:bg-blue-700" : "border-slate-600"}
                  onClick={() => saveTranslationSettings({
                    ...autoTranslateSettings,
                    auto_translate_enabled: !autoTranslateSettings.auto_translate_enabled,
                  })}
                >
                  {autoTranslateSettings.auto_translate_enabled ? "ON" : "OFF"}
                </Button>
              </div>
              {autoTranslateSettings.auto_translate_enabled && (
                <div className="pl-4 pt-2">
                  <label className="text-sm text-slate-400 block mb-1">Idioma destino:</label>
                  <Select
                    value={autoTranslateSettings.target_language}
                    onValueChange={(v) => saveTranslationSettings({ ...autoTranslateSettings, target_language: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="pt">Português</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="zh">中文</SelectItem>
                      <SelectItem value="ja">日本語</SelectItem>
                      <SelectItem value="ko">한국어</SelectItem>
                      <SelectItem value="ru">Русский</SelectItem>
                      <SelectItem value="ar">العربية</SelectItem>
                      <SelectItem value="hi">हिन्दी</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Auto Translate */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Auto-traducir</p>
                  <p className="text-sm text-slate-400">Traducir mensajes de voz</p>
                </div>
                <Button
                  variant={voiceSettings.autoTranslate ? "default" : "outline"}
                  size="sm"
                  className={voiceSettings.autoTranslate ? "bg-red-600" : "border-slate-600"}
                  onClick={() => setVoiceSettings({...voiceSettings, autoTranslate: !voiceSettings.autoTranslate})}
                >
                  {voiceSettings.autoTranslate ? "ON" : "OFF"}
                </Button>
              </div>
              
              {voiceSettings.autoTranslate && (
                <div className="pl-4 pt-2">
                  <label className="text-sm text-slate-400 block mb-1">Idioma destino:</label>
                  <Select
                    value={voiceSettings.translateTo}
                    onValueChange={(value) => setVoiceSettings({...voiceSettings, translateTo: value})}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 w-full">
                      <SelectValue placeholder="Seleccionar idioma" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">Inglés</SelectItem>
                      <SelectItem value="pt">Portugués</SelectItem>
                      <SelectItem value="fr">Francés</SelectItem>
                      <SelectItem value="de">Alemán</SelectItem>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="zh">Chino</SelectItem>
                      <SelectItem value="ja">Japonés</SelectItem>
                      <SelectItem value="ko">Coreano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Voice Filter Info */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-sm text-slate-300 mb-2">
                <strong className="text-red-400">Filtros de voz</strong> se aplican al mensaje Walkie-Talkie.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>🤖 <strong>Robot:</strong> Voz distorsionada electrónica</li>
                <li>🔊 <strong>Eco:</strong> Efecto de reverberación</li>
                <li>🐿️ <strong>Ardilla:</strong> Voz aguda tipo chipmunk</li>
                <li>🔈 <strong>Grave:</strong> Voz profunda</li>
              </ul>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button onClick={() => setShowVoiceSettings(false)} className="bg-red-600 hover:bg-red-700">
              Listo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Filter Modal */}
      <Dialog open={showVoiceFilterModal} onOpenChange={setShowVoiceFilterModal}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-500" />
              Filtro de Voz
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {[
              { id: "normal", label: "Normal", icon: "🎤", desc: "Sin filtro" },
              { id: "robot", label: "Robot", icon: "🤖", desc: "Voz robótica distorsionada" },
              { id: "echo", label: "Eco", icon: "🔊", desc: "Efecto de reverberación" },
              { id: "squirrel", label: "Ardilla", icon: "🐿️", desc: "Voz aguda tipo chipmunk" },
              { id: "deep", label: "Grave", icon: "🔈", desc: "Voz profunda" },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => {
                  setSelectedVoiceFilter(filter.id);
                  setShowVoiceFilterModal(false);
                }}
                className={`w-full p-3 rounded-lg border transition-all flex items-center gap-3 ${
                  selectedVoiceFilter === filter.id 
                    ? "bg-purple-600/20 border-purple-500 text-white" 
                    : "bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600"
                }`}
              >
                <span className="text-2xl">{filter.icon}</span>
                <div className="text-left">
                  <p className="font-medium">{filter.label}</p>
                  <p className="text-xs text-slate-400">{filter.desc}</p>
                </div>
                {selectedVoiceFilter === filter.id && (
                  <Check className="w-5 h-5 text-purple-400 ml-auto" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Translator Modal */}
      <Dialog open={showTranslatorModal} onOpenChange={setShowTranslatorModal}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="w-5 h-5 text-blue-500" />
              Traductor
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
              <div>
                <p className="text-white font-medium">Auto-traducir</p>
                <p className="text-sm text-slate-400">Traducir mensajes automáticamente</p>
              </div>
              <Button
                variant={voiceSettings.autoTranslate ? "default" : "outline"}
                size="sm"
                className={voiceSettings.autoTranslate ? "bg-blue-600 hover:bg-blue-700" : "border-slate-600"}
                onClick={() => setVoiceSettings({...voiceSettings, autoTranslate: !voiceSettings.autoTranslate})}
              >
                {voiceSettings.autoTranslate ? "ON" : "OFF"}
              </Button>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Idioma de destino:</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { code: "es", flag: "🇪🇸", name: "Español" },
                  { code: "en", flag: "🇺🇸", name: "Inglés" },
                  { code: "pt", flag: "🇧🇷", name: "Portugués" },
                  { code: "fr", flag: "🇫🇷", name: "Francés" },
                  { code: "de", flag: "🇩🇪", name: "Alemán" },
                  { code: "it", flag: "🇮🇹", name: "Italiano" },
                  { code: "zh", flag: "🇨🇳", name: "Chino" },
                  { code: "ja", flag: "🇯🇵", name: "Japonés" },
                  { code: "ko", flag: "🇰🇷", name: "Coreano" },
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setVoiceSettings({...voiceSettings, translateTo: lang.code})}
                    className={`p-2 rounded-lg border text-center transition-all ${
                      voiceSettings.translateTo === lang.code
                        ? "bg-blue-600/20 border-blue-500"
                        : "bg-slate-800 border-slate-700 hover:border-slate-600"
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <p className="text-xs text-slate-300 mt-1">{lang.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button onClick={() => setShowTranslatorModal(false)} className="bg-blue-600 hover:bg-blue-700">
              Listo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="bg-[#1a1a24] border-slate-700 text-white max-w-lg max-h-[70vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" />
              Historial de Conversación
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {messages.slice(-50).map((msg) => (
                <div 
                  key={msg.message_id}
                  className={`p-3 rounded-lg ${
                    msg.sender_id === user?.user_id 
                      ? "bg-red-600/20 border border-red-600/30 ml-8" 
                      : "bg-slate-800 mr-8"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-red-400">
                      {msg.sender_info?.name || "Usuario"}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300">
                    {msg.message_type === "walkie_talkie" && "🎤 "}
                    {msg.message_type === "normal" && "🎙️ "}
                    {msg.content || (msg.message_type?.includes("image") ? "[Imagen]" : "[Archivo]")}
                  </p>
                  {msg.transcription && (
                    <p className="text-xs text-slate-400 mt-1 italic">
                      "{msg.transcription}"
                    </p>
                  )}
                </div>
              ))}
              {messages.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay mensajes en esta conversación</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <div className="flex justify-end">
            <Button onClick={() => setShowHistoryModal(false)} variant="outline" className="border-slate-600 text-slate-300">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Media Caption Preview Modal (WhatsApp-style) =====
          Aparece después de subir una imagen/video/documento. Muestra preview
          y permite escribir un mensaje opcional antes de enviar. */}
      <Dialog open={!!mediaPreview} onOpenChange={(open) => { if (!open) cancelMediaPreview(); }}>
        <DialogContent className="bg-[#1a1d24] border-[#2a2d35] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Enviar archivo</DialogTitle>
          </DialogHeader>
          {mediaPreview && (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden bg-black flex items-center justify-center max-h-80">
                {mediaPreview.fileType === "image" && (
                  <img src={mediaPreview.localUrl} alt="preview" className="max-h-80 max-w-full object-contain" />
                )}
                {mediaPreview.fileType === "video" && (
                  <video src={mediaPreview.localUrl} className="max-h-80 max-w-full" controls />
                )}
                {mediaPreview.fileType === "document" && (
                  <div className="flex items-center gap-3 p-6 w-full">
                    <FileText className="w-10 h-10 text-red-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{mediaPreview.fileData?.file_name || mediaPreview.originalName}</p>
                      <p className="text-xs text-slate-400">{formatFileSize(mediaPreview.fileData?.file_size)}</p>
                    </div>
                  </div>
                )}
              </div>
              <textarea
                placeholder="Añadir un mensaje (opcional)..."
                value={mediaCaption}
                onChange={(e) => setMediaCaption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    confirmSendMedia();
                  }
                }}
                rows={2}
                className="w-full bg-[#252830] text-white placeholder:text-slate-500 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button onClick={cancelMediaPreview} variant="outline" className="border-slate-600 text-slate-300">
                  Cancelar
                </Button>
                <Button onClick={confirmSendMedia} className="bg-red-600 hover:bg-red-700">
                  <Send className="w-4 h-4 mr-2" /> Enviar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== GIF Caption Preview Modal ===== */}
      <Dialog open={!!gifPreview} onOpenChange={(open) => { if (!open) cancelGifPreview(); }}>
        <DialogContent className="bg-[#1a1d24] border-[#2a2d35] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Enviar GIF</DialogTitle>
          </DialogHeader>
          {gifPreview && (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden bg-black flex items-center justify-center max-h-80">
                <img src={gifPreview.url} alt="GIF preview" className="max-h-80 max-w-full object-contain" />
              </div>
              <textarea
                placeholder="Añadir un mensaje (opcional)..."
                value={gifCaption}
                onChange={(e) => setGifCaption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    confirmSendGif();
                  }
                }}
                rows={2}
                className="w-full bg-[#252830] text-white placeholder:text-slate-500 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button onClick={cancelGifPreview} variant="outline" className="border-slate-600 text-slate-300">
                  Cancelar
                </Button>
                <Button onClick={confirmSendGif} className="bg-red-600 hover:bg-red-700">
                  <Send className="w-4 h-4 mr-2" /> Enviar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product picker (in-chat commerce) */}
      <ProductPickerDialog
        open={showProductPicker}
        onOpenChange={setShowProductPicker}
        conversationId={selectedConversation?.conversation_id}
        onSent={(msg) => {
          if (msg && msg.message_id) {
            setMessages((prev) => prev.some((m) => m.message_id === msg.message_id) ? prev : [...prev, msg]);
          }
        }}
      />

      {/* AI conversation summary */}
      <ConversationSummaryDialog
        open={showSummaryDialog}
        onOpenChange={setShowSummaryDialog}
        conversationId={selectedConversation?.conversation_id}
        conversationName={selectedConversation ? getConversationName(selectedConversation) : ""}
      />

      {/* Wave 2: scheduled messages, stickers, mini-apps, premium upgrade */}
      <ScheduledMessagesDialog
        open={showScheduledDialog}
        onOpenChange={setShowScheduledDialog}
        conversationId={selectedConversation?.conversation_id}
      />
      <StickerPickerDialog
        open={showStickerDialog}
        onOpenChange={setShowStickerDialog}
        conversationId={selectedConversation?.conversation_id}
      />
      <MiniAppPickerDialog
        open={showMiniAppDialog}
        onOpenChange={setShowMiniAppDialog}
        conversationId={selectedConversation?.conversation_id}
      />
      <PremiumUpgradeDialog
        open={showPremiumDialog}
        onOpenChange={setShowPremiumDialog}
      />
      <DevicesDialog open={showDevicesDialog} onOpenChange={setShowDevicesDialog} />
      <GamificationDialog open={showGamificationDialog} onOpenChange={setShowGamificationDialog} />
      {activeCall && (
        <VoiceCallDialog
          open={!!activeCall}
          onOpenChange={(v) => { if (!v) setActiveCall(null); }}
          mode={activeCall.mode}
          peer={activeCall.peer}
          callId={activeCall.callId}
          wsRef={ws}
          incomingOffer={activeCall.incomingOffer}
          selfUser={{ user_id: user?.user_id, name: user?.name, picture: user?.picture }}
        />
      )}
    </div>
  );
};

export default ChatPage;
